import { getBootProperties } from 'src/firebase/bootSession';
import {
  ChartPieIcon,
  ChatBubbleLeftRightIcon,
  PhotoIcon,
  ShoppingBagIcon,
  UserPlusIcon,
} from '@heroicons/react/24/solid';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import { resolveSeatPriceCents } from 'src/providers/SubscriptionProvider/useSeatPurchase.functions';
import { AxiosError } from 'axios';
import { FormikConfig, useFormik } from 'formik';
import { isEmpty, isUndefined } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RouterPaths } from 'src/components/Router/Router.constants.ts';
import { useWalletFlag } from 'src/pages/Wallet/hooks/useWalletFlag';
import {
  ePlanCode,
  ePlanFrequency,
  eSubscriptionStatus,
} from 'src/providers/SubscriptionProvider/Subscription.enums';
import { isValidUUID } from 'src/utils/isValidUUID';
import {
  resolveBillingErrorMessage,
  stripeJsErrorToBillingPayload,
  type tBillingErrorPayload,
} from 'src/utils/resolveBillingError';
import LeadbotIcon from '../../../../assets/leadbot.svg';
import whatsAppLogoPurple from '../../../../assets/whatsAppLogoPurple.svg';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { useSubscriptionAnalytics } from 'src/hooks/analytics/subscription/useSubscriptionAnalytics.hook';
import { SUBSCRIPTION_EVENTS } from 'src/hooks/analytics/subscription/subscriptionAnalytics.constants';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from '../../../../lib/logging';
import { useACLContext } from '../../../../providers/ACLProvider/ACL.hook';
import { useFeatureFlagsContext } from 'src/providers/FeatureFlagsProvider/FeatureFlags.context';
import {
  eIntegrationStatus,
  eIntegrationType,
} from '../../../../providers/IntegrationsProvider/Integrations.enums';
import { IntegrationsService } from '../../../../providers/IntegrationsProvider/Integrations.service';
import { useSubscriptionContext } from '../../../../providers/SubscriptionProvider/Subscription.context';
import SubscriptionService from '../../../../providers/SubscriptionProvider/Subscription.service';
import { BillingError } from '../../../../providers/SubscriptionProvider/Subscription.types';
import { eAlertType } from '../../../../providers/ToastProvider/Toast.enum';
import { useToastContext } from '../../../../providers/ToastProvider/Toast.hook';
import { useUserContext } from '../../../../providers/UserProvider/User.context';
import { useWorkspaceContext } from '../../../../providers/WorkspaceProvider/Workspace.context';
import {
  trackAddToCartEvent,
  trackPurchaseEvent,
} from '../../../../utils/gtm/gtm.service';
import { DashboardService } from '../../../Dashboard/Dashboard.service';
import { useSettingsContext } from '../../Settings.context';
import { SettingsTabs } from '../../Settings.enum';
import {
  INITIAL_PAYMENT_METHOD,
  LEAD_LIMIT_ADDON_INCREMENT,
  OPEN_ADVANCED_LEADBOT_ADDON_PARAM,
  OWNER_REQUIRED_REFUSAL_CODES,
  REDIRECT_RETURN_GATE_TIMEOUT_MS,
  SUBSCRIPTION_MANAGER_INITIAL_STATE,
} from './SubscriptionManager.constants';
import { normalizeCouponDiscountValue } from './SubscriptionManager.functions';
import {
  ePlanCurrency,
  ePlanType,
  eProductType,
} from './SubscriptionManager.enums';
import {
  buildInvoicePreviewRequest,
  clearRedirectReturnSnapshot,
  extractPaymentIntentData,
  extractSetupIntentPaymentMethodId,
  findCatalogProduct,
  getPlanName,
  hasCatalogProduct,
  isStripeErrorCorrectableInForm,
  isSubscriptionPastDue,
  PAYMENT_METHOD_ADDED_TITLE_KEY,
  PAYMENT_METHOD_SAVED_WITHOUT_DEFAULT_KEY,
  readSetupIntentReturn,
  REDIRECT_RETURN_DECLINED_KEY,
  REDIRECT_RETURN_NOT_CONFIRMED_KEY,
  resolvePaymentMethodDefaultOutcome,
  resolveAddPaymentMethodSuccessNotice,
  resolvePaymentMethodSelection,
  resolvePaymentMethodDefaultRefusalNotice,
  retrieveSetupIntentWithin,
  retryUntil,
  setupIntentIdFromClientSecret,
  shouldFetchInvoicePreview,
  shouldReactivateInPlace,
  shouldUseUpgradeContext,
  withoutSetupIntentReturn,
  writeRedirectReturnSnapshot,
} from './SubscriptionManager.functions';
import { CancelSubscriptionSchema } from './SubscriptionManager.schemas';
import { usePastDuePayment } from './PastDuePayment.hook';
import { brandName } from './components/SubscriptionOverview/PastDuePaymentZone.component';
import { formatAmountOwed } from 'src/utils/pastDueDisclosure';
import {
  PaymentMethodDefaultRefusedError,
  readBillingRefusalCode,
  SubscriptionManagerService,
} from './SubscriptionManager.service';
import {
  tAddOn,
  tCancelForm,
  tAddPaymentMethodOptions,
  tCardSavedForCaller,
  tFinishConfirmedCardAddOptions,
  tFinishConfirmedCardAddOutcome,
  tRedirectReturnProcess,
  tInvoicePreview,
  tPaymentMethod,
  tPaymentMethodDefaults,
  tPaymentMethodsLoadState,
  tPreviousPaymentMethods,
  tPaymentMethodOption,
  tPlan,
  tProducts,
  tProductToPurchase,
  tRelatedProduct,
  tSubscriptionUpgradeRequest,
  tUseSubscriptionManagerContext,
} from './SubscriptionManager.types';
import { tCreditProductsOption } from './components/SubscriptionCheckout/CheckoutSummary/CheckoutSummary.types';

/**
 * Normaliza el codigo de plan del backend a la etiqueta corta usada en los
 * eventos de analytics (`basic`, `professional`, `advanced`). Codigos
 * desconocidos se devuelven sin transformar para no perder informacion.
 *
 * @param planCode Codigo de plan crudo (por ejemplo `PLAN_PROFESSIONAL`).
 * @returns La etiqueta normalizada o el codigo original si no hay coincidencia.
 */
function mapPlanCodeToPlanType(planCode: string): string {
  switch (planCode) {
    case ePlanCode.PLAN_BASIC:
      return 'basic';
    case ePlanCode.PLAN_PROFESSIONAL:
      return 'professional';
    case ePlanCode.PLAN_ENTERPRISE:
      return 'advanced';
    default:
      return planCode;
  }
}

function useSubscriptionManager(): tUseSubscriptionManagerContext {
  const { currentWorkspace, workspaceInfo, activateWorkspace } =
    useWorkspaceContext();
  // RET-2428: the workspace of the latest render, read after an await to drop
  // an answer that belongs to the workspace the user just left.
  const currentWorkspaceIdRef = useRef<string | undefined>(currentWorkspace?.id);
  currentWorkspaceIdRef.current = currentWorkspace?.id;
  const { currentSettingsInfo, setCurrentSettingsInfo } = useSettingsContext();
  const { trackEvent } = useAnalytics();
  const { trackSubscriptionEvent } = useSubscriptionAnalytics();
  const stripe = useStripe();
  const elements = useElements();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const {
    subscription,
    isClient,
    hasSubscriptionData,
    plans,
    getSummary,
    getPlans,
    cancelCurrentSubscription,
    daysPastDue,
    isLoadingSubscription,
  } = useSubscriptionContext();
  // RET-2452: the past-due payment zone has its own flag, per workspace.
  const { isPastDuePaymentZoneEnabled } = useFeatureFlagsContext();
  const { t, ready: translationsReady } = useTranslation(
    'settings/SubscriptionManager',
  );
  const { addAlert } = useToastContext();
  const { currentUser } = useUserContext();
  const {
    watermarkRemoval,
    watermarkRemovalId,
    leadbotLevelsAccess,
    leadbotLevelsAccessId,
    leadLimitAddonId,
    isAdvancedPlan,
    isBusinessPlan,
    isGrowthPlan,
    isLegacyPlan,
    // RET-2428: the plan's status, not the checkout's `isTrial` state below.
    isTrial: isTrialSubscription,
    userIsOwner,
    userIsAdmin,
  } = useACLContext();
  // RET-1961 (N4/P9): the credits product is a SKU the wallet no longer
  // sells — under the flag it must never reach a purchase/upgrade/preview
  // payload, even if a stale selection is still sitting in `monthlyCredits`.
  const { isWalletEnabled } = useWalletFlag();

  const [showAddPaymentMethodModal, setShowAddPaymentMethodModal] =
    useState<boolean>(false);
  // RET-2426: what the caller of `openAddPaymentMethodModal` asked of the add.
  // See `tAddPaymentMethodOptions`.
  const [addPaymentMethodOptions, setAddPaymentMethodOptions] =
    useState<tAddPaymentMethodOptions | null>(null);
  // RET-2426: the card an add opened with a `requestId` saved. See its
  // docblock in the context type.
  const [cardSavedForCaller, setCardSavedForCaller] =
    useState<tCardSavedForCaller>({
      token: 0,
      requestId: null,
      paymentMethodId: null,
    });
  // Every plain open or close drops the caller's options: an add opened from
  // anywhere else (Settings, Recargar, the add-ons) is the plain add, with the
  // usual button and the usual `PUT …/default`. Closing drops them too, so they
  // never outlive the modal they were set for. `addPaymentMethod` reads them
  // once, when the save starts.
  const toggleAddPaymentMethodModal = useCallback((toggle: boolean) => {
    setAddPaymentMethodOptions(null);
    setShowAddPaymentMethodModal(toggle);
  }, []);
  const openAddPaymentMethodModal = useCallback(
    (options: tAddPaymentMethodOptions) => {
      setAddPaymentMethodOptions(options);
      setShowAddPaymentMethodModal(true);
    },
    [],
  );
  const [showPaymentCompletedModal, togglePaymentCompletedModal] =
    useState<boolean>(false);
  const [isTrial, setIsTrial] = useState<boolean>(false);
  const [showPaymentMethodsManager, togglePaymentMethodsManager] =
    useState<boolean>(false);
  const [showCheckoutView, toggleCheckoutView] = useState<boolean>(false);
  const [showPlansView, setShowPlansViewState] = useState<boolean>(false);
  const [showOnlyWAPIPlans, setShowOnlyWAPIPlans] = useState<boolean>(false);
  const upgradeProcessedRef = useRef<boolean>(false);
  // RET-2028: `upgradeProcessedRef` used to be write-once for the whole
  // session (set true, never reset), so any CLOSE of the plans view left the
  // ?upgrade=true deep link permanently ignored on a later arrival — the same
  // "once per view" antipattern as
  // docs/solutions/logic-errors/once-per-view-analytics-guard-locks-in-preload-zeros.md,
  // just gating a toggle instead of an analytics emit. Every path that closes
  // the plans view (the X, returnToOverview(), and the Settings-unmount reset
  // in SubscriptionManager.component.tsx) already calls this setter, so
  // routing the close through it here — instead of exposing a second,
  // easy-to-forget reset function — is what makes "closed" and "the deep
  // link can fire again" the same fact. Opening (true) is untouched.
  // useCallback keeps this reference-stable so it's safe to sit in the
  // ?upgrade=true effect's dependency array below (SubscriptionManager
  // .component.tsx's unmount cleanup also depends on it staying stable).
  const togglePlansView = useCallback((value: boolean) => {
    if (!value) {
      upgradeProcessedRef.current = false;
    }
    setShowPlansViewState(value);
  }, []);
  const [products, setProducts] = useState<tProducts | null>(null);
  const [showTrialCheckoutView, toggleTrialCheckoutView] =
    useState<boolean>(false);
  const [extraSeats, setExtraSeats] = useState<number>(
    subscription.purchasedSeats,
  );
  const [monthlyCredits, setMonthlyCredits] =
    useState<tCreditProductsOption | null>(null);
  const [updatingPlan, toggleUpdatingPlan] = useState<boolean>(false);
  const [period, setPeriod] = useState<ePlanFrequency>(ePlanFrequency.MONTHLY);
  const [planToBuy, setPlanToBuy] = useState<tPlan | null>(null);
  const [addingPaymentMethod, setAddingPaymentMethod] =
    useState<boolean>(false);
  // RET-2348: goes up each time `addPaymentMethod` saves a card and promotes
  // it to the account's default. See its docblock in the context type.
  const [cardSavedAsDefaultToken, setCardSavedAsDefaultToken] =
    useState<number>(0);
  // RET-2428: the id of the card behind the last bump of the token above, so
  // the trial confirmation selects the card just added in both outcomes.
  const [
    cardSavedAsDefaultPaymentMethodId,
    setCardSavedAsDefaultPaymentMethodId,
  ] = useState<string | null>(null);
  // RET-2428: goes up each time `addPaymentMethod` saves a card that billing
  // kept WITHOUT making it the default (a trial not confirmed yet). See its
  // docblock in the context type.
  const [cardSavedWithoutDefaultToken, setCardSavedWithoutDefaultToken] =
    useState<number>(0);
  // RET-2428: the id of the card behind the last bump of the token above.
  const [
    cardSavedWithoutDefaultPaymentMethodId,
    setCardSavedWithoutDefaultPaymentMethodId,
  ] = useState<string | null>(null);
  const [processingPayment, toggleProcessingPayment] = useState<boolean>(false);
  const [couponCode, setCouponCode] = useState<string>('');
  const [appliedCouponDiscount, setAppliedCouponDiscount] = useState<number | null>(null);
  const [couponDiscountType, setCouponDiscountType] = useState<'percentage' | 'fixed_amount' | null>(null);
  const [couponDuration, setCouponDuration] = useState<'once' | 'forever' | 'repeating' | null>(null);
  const [couponDurationInMonths, setCouponDurationInMonths] = useState<number | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [applyingCoupon, setApplyingCoupon] = useState<boolean>(false);
  // RET-1157 / JFR-001: live mirror of isUpgradeContext for in-flight applyCoupon
  // continuations. The closure captured at handler creation time is stale by the
  // resolution of the await — this ref is kept current via a useEffect below and
  // is checked after each await to avoid writing coupon state into a hidden UI.
  const isUpgradeContextRef = useRef<boolean>(false);
  // RET-1157 / JFR-004: AbortController for the in-flight validateCoupon network
  // call. Aborts on re-apply, on removeCoupon, and on hook unmount so stale
  // resolutions cannot land on orphaned state.
  const couponAbortRef = useRef<AbortController | null>(null);
  // RET-1285: per-attempt purchase idempotency intent. Held in a ref so it is
  // mid-await safe (the value read before the first purchaseProduct call is the
  // same value reused by the post-3DS re-call, regardless of any re-render in
  // between). Generated lazily on the first purchase of an attempt and cleared
  // in returnFromCheckout so a deliberate new purchase gets a fresh intent.
  const purchaseIntentIdRef = useRef<string>('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<tPaymentMethod>(INITIAL_PAYMENT_METHOD);
  const [paymentMethods, setCurrentPaymentMethods] = useState<tPaymentMethod[]>(
    [],
  );
  // RET-2428: whether `paymentMethods` is the answer of a fetch, so an empty
  // list can be told apart from a list nobody has read yet (or failed to).
  const [paymentMethodsLoadState, setPaymentMethodsLoadState] =
    useState<tPaymentMethodsLoadState>(
      SUBSCRIPTION_MANAGER_INITIAL_STATE.paymentMethodsLoadState,
    );
  // RET-2027: the plan's card and the customer default, as billing answered
  // them. Kept apart from `selectedPaymentMethod`, which is what checkout,
  // debt payment and the wallet top-up modal charge or show.
  const [paymentMethodDefaults, setPaymentMethodDefaults] =
    useState<tPaymentMethodDefaults>({});
  // RET-2428: the card that paid the trial, as the last list fetch answered.
  const [trialChargePaymentMethodId, setTrialChargePaymentMethodId] = useState<
    string | null
  >(SUBSCRIPTION_MANAGER_INITIAL_STATE.trialChargePaymentMethodId);
  // RET-2364: the plan's card and the account default as they were when the
  // plan-change checkout opened, sent with the payment so billing can put them
  // back if the bank refuses the charge. A ref, not state: saving or picking a
  // card in the checkout re-reads `paymentMethodDefaults` (the card becomes the
  // default, RET-1973), and this must keep what was there BEFORE that. It is
  // overwritten every time the checkout opens, empty included, so a previous
  // checkout's cards never travel with this one.
  const previousPaymentMethodsRef = useRef<tPreviousPaymentMethods>({
    plan: null,
    account: null,
  });
  // RET-1974 (R7): the card the customer chose while the checkout is open.
  // The mount effect's re-read prefers it over the provider default, so a
  // background refetch does not put the selection back on a card that failed
  // when the new one could not be made the default. Only the checkout keeps
  // it: elsewhere the top-up and the add-ons charge the default and must show
  // it (`RC-16`). The paths that follow the default on purpose clear it. It
  // names its workspace: on a switch the mount re-read runs before the reset.
  const preferredPaymentMethodRef = useRef<
    { workspaceId: string; paymentMethodId: string } | undefined
  >(undefined);
  // RET-1974: a card add that came back from the bank's page, copied out of
  // the URL as soon as it is read (the URL is cleaned right away), and how far
  // its handling got. One intent is handled once, whatever re-renders.
  const redirectReturnRef = useRef<tRedirectReturnProcess | null>(null);
  // RET-1974: on while a return is handled. Another add, a card pick or a
  // delete would cross its promotion, so they are ignored until it ends.
  const redirectReturnPendingRef = useRef<boolean>(false);
  // RET-1974: moves when the handler stops waiting for what it needs, so the
  // effect that handles the return runs once more.
  const [redirectReturnCeilingTick, setRedirectReturnCeilingTick] =
    useState<number>(0);
  const [loadingPaymentMethodRequest, togglePaymentMethodRequest] =
    useState(false);
  const [showManageUsersModal, toggleManageUsersModal] =
    useState<boolean>(false);
  const [showManageWatermarkModal, toggleManageWatermarkModal] =
    useState<boolean>(false);
  const [showManageAdvancedLeadbotModal, toggleManageAdvancedLeadbotModal] =
    useState<boolean>(false);
  const [showManageLeadLimitAddonModal, toggleManageLeadLimitAddonModal] =
    useState<boolean>(false);
  const [showCancelSubscriptionModal, toggleCancelSubscriptionModal] =
    useState<boolean>(false);
  const [cancellingSubscription, toggleCancellingSubscription] =
    useState<boolean>(false);
  const [reactivatingSubscription, toggleReactivatingSubscription] =
    useState<boolean>(false);
  const [paymentMethodsOptions, setPaymentMethodsOptions] = useState<
    tPaymentMethodOption[]
  >([]);

  const [selectedAddonProducts, setSelectedAddonProducts] = useState<
    tRelatedProduct[]
  >([]);
  const [invoicePreview, setInvoicePreview] = useState<tInvoicePreview | null>(
    null,
  );

  // RET-2452 (KTD-3): the past-due payment zone. Its state (phase, the zone's
  // own card selection, the decline of a payment made here) lives in
  // `usePastDuePayment`; this hook only feeds it and publishes the view, and
  // only with `billing_past-due-payment-zone` on.
  const { pastDuePayment, isPastDueZoneActive, selectPastDueCard } =
    usePastDuePayment({
    workspaceId: currentWorkspace?.id,
    enabled: isPastDuePaymentZoneEnabled,
    canPay: userIsOwner === true || userIsAdmin === true,
    subscription,
    summaryState: hasSubscriptionData
      ? 'loaded'
      : isLoadingSubscription
        ? 'loading'
        : 'failed',
    daysPastDue: daysPastDue ?? null,
    paymentMethods,
    paymentMethodsLoaded: paymentMethodsLoadState === 'loaded',
    paymentMethodDefaults,
    readReturnSetupIntentId: () =>
      redirectReturnRef.current?.setupIntentId ?? null,
    stripe,
    refreshSummary: getSummary,
    refreshPaymentMethods: () => retrievePaymentMethods(),
    openAddPaymentMethod: () => toggleAddPaymentMethodModal(true),
    notify: addAlert,
    translate: (key, values) => t(key, values),
  });

  // Helper function to convert frequency to months for MRR calculation
  const getFrequencyInMonths = (frequency: ePlanFrequency): number => {
    switch (frequency) {
      case ePlanFrequency.MONTHLY:
        return 1;
      case ePlanFrequency.QUARTERLY:
        return 3;
      case ePlanFrequency.BIANNUAL:
        return 6;
      case ePlanFrequency.ANNUALLY:
        return 12;
      case ePlanFrequency.WEEKLY:
        return 0.25; // 4 weeks = 1 month
      case ePlanFrequency.TRIAL:
      default:
        return 1; // Default to monthly
    }
  };

  // Calculate MRR in USD
  // Formula: ((subscription_price/21/1.16)/user_plan_reccurency_type)
  // If MXN: convert to USD first, then divide by recurrence type
  // If USD: divide by recurrence type directly
  const calculateMRR = (): number => {
    const subscriptionPriceInDollars = subscription.totalAmount / 100;
    const frequencyInMonths = getFrequencyInMonths(subscription.frequency);

    // If currency is MXN, convert to USD: (price / 21 / 1.16)
    // Then divide by recurrence type to get monthly MRR
    if (subscription.currency === ePlanCurrency.MXN) {
      const priceInUSD = subscriptionPriceInDollars / 21 / 1.16;
      return priceInUSD / frequencyInMonths;
    }

    // If currency is USD, divide by recurrence type directly
    return subscriptionPriceInDollars / frequencyInMonths;
  };

  const mapSubscriptionStatus = (
    status: eSubscriptionStatus,
  ): string => {
    const statusMap: Record<eSubscriptionStatus, string> = {
      [eSubscriptionStatus.TRIAL]: 'trial',
      [eSubscriptionStatus.ACTIVE]: 'active',
      [eSubscriptionStatus.PAST_DUE]: 'past_due',
      [eSubscriptionStatus.NO_PAID]: 'no_paid',
      [eSubscriptionStatus.CANCELLED]: 'cancelled',
      [eSubscriptionStatus.DELETED]: 'deleted',
      [eSubscriptionStatus.SCHEDULED_CANCELLATION]: 'scheduled_cancellation',
      [eSubscriptionStatus.PAUSED]: 'paused',
      [eSubscriptionStatus.INACTIVE]: 'inactive',
    };
    return statusMap[status] || 'unknown';
  };

  const generalCancellationProperties = {
    user_email: currentUser?.email ?? '',
    user_plan: subscription.planCode.toString(),
    user_plan_recurrency_type: subscription.frequency.toString(),
    user_mrr: calculateMRR(),
    workspace_id: currentWorkspace?.id ?? '',
    subscription_status: mapSubscriptionStatus(subscription.status),
    timestamp: new Date().toISOString(),
  };

  // Reset WAPI-only filter when the plans view closes (guard prevents no-op on mount)
  useEffect(() => {
    if (!showPlansView && showOnlyWAPIPlans) setShowOnlyWAPIPlans(false);
  }, [showPlansView, showOnlyWAPIPlans]);

  // Auto-select watermark product if current plan has watermark removal enabled
  useEffect(() => {
    if (
      planToBuy &&
      watermarkRemoval &&
      watermarkRemovalId &&
      showCheckoutView
    ) {
      // Find the watermark product in the plan's related products
      const watermarkProduct = planToBuy.relatedProducts.find(
        (product) => product.productType === 'LEADBOT_PREMIUM',
      );

      if (watermarkProduct) {
        // Check if it's not already selected
        const isAlreadySelected = selectedAddonProducts.some(
          (selectedProduct) => selectedProduct.uuid === watermarkProduct.uuid,
        );

        if (!isAlreadySelected) {
          setSelectedAddonProducts((prevProducts) => [
            ...prevProducts,
            watermarkProduct,
          ]);
        }
      }
    }
  }, [
    planToBuy,
    watermarkRemoval,
    watermarkRemovalId,
    selectedAddonProducts,
    showCheckoutView,
  ]);

  // Auto-select Advanced Leadbot product if current plan has it enabled
  useEffect(() => {
    if (
      planToBuy &&
      leadbotLevelsAccess &&
      leadbotLevelsAccessId &&
      showCheckoutView
    ) {
      // Find the Advanced Leadbot product in the plan's related products
      const leadbotProduct = planToBuy.relatedProducts.find(
        (product) => product.productType === eProductType.LEADBOT_LEVELS,
      );

      if (leadbotProduct) {
        // Check if it's not already selected
        const isAlreadySelected = selectedAddonProducts.some(
          (selectedProduct) => selectedProduct.uuid === leadbotProduct.uuid,
        );

        if (!isAlreadySelected) {
          setSelectedAddonProducts((prevProducts) => [
            ...prevProducts,
            leadbotProduct,
          ]);
        }
      }
    }
  }, [
    planToBuy,
    leadbotLevelsAccess,
    leadbotLevelsAccessId,
    selectedAddonProducts,
    showCheckoutView,
  ]);

  // Auto-select Lead Limit Addon product if current plan has it enabled
  useEffect(() => {
    if (
      planToBuy &&
      workspaceInfo?.isActive &&
      products &&
      isLeadLimitAddonActive() &&
      showCheckoutView
    ) {
      // Find the Lead Limit Addon product in the plan's related products
      const leadLimitAddonProduct = planToBuy.relatedProducts.find(
        (product) => product.productType === eProductType.LEAD_LIMIT_ADDON,
      );

      if (leadLimitAddonProduct) {
        // Check if it's not already selected
        const isAlreadySelected = selectedAddonProducts.some(
          (selectedProduct) =>
            selectedProduct.uuid === leadLimitAddonProduct.uuid,
        );

        if (!isAlreadySelected) {
          setSelectedAddonProducts((prevProducts) => [
            ...prevProducts,
            leadLimitAddonProduct,
          ]);
        }
      }
    }
  }, [
    planToBuy,
    workspaceInfo?.isActive,
    products,
    selectedAddonProducts,
    showCheckoutView,
  ]);

  const [loadingInvoicePreview, setLoadingInvoicePreview] =
    useState<boolean>(false);
  const baseMonthlyCredits = planToBuy?.features.templateCreditsLimit;

  // Handle upgrade query param to show plans view
  useEffect(() => {
    const upgradeParam = searchParams.get('upgrade');
    if (upgradeParam === 'true' && !upgradeProcessedRef.current) {
      // Mark as processed to prevent re-processing
      upgradeProcessedRef.current = true;

      // Set plans view to true regardless of subscription availability
      togglePlansView(true);

      // Cancelled subscribers must see only WAPI plans (same rule as the Reactivate button)
      if (subscription?.status === eSubscriptionStatus.CANCELLED) {
        setShowOnlyWAPIPlans(true);
      }

      // Set period if subscription is available
      if (subscription?.frequency) {
        setPeriod(subscription.frequency);
      }

      // Remove the query param after a short delay to ensure state is set
      const timeoutId = setTimeout(() => {
        searchParams.delete('upgrade');
        setSearchParams(searchParams, { replace: true });
      }, 100);

      return () => clearTimeout(timeoutId);
    }
    // RET-2028: depend on the two primitives this effect actually reads, not
    // the whole `subscription` object. `subscription` is a fresh reference on
    // every render whose data changes (getSummary() refetches, workspace
    // switches, and — in tests — a context mock that isn't memoized), so
    // depending on the object made this effect re-evaluate on renders that
    // have nothing to do with a new ?upgrade=true arrival. That is harmless
    // while `upgradeProcessedRef` never resets, but now that closing the
    // plans view legitimately resets it (see `togglePlansView` above), an
    // incidental re-render with the SAME stale `?upgrade=true` still sitting
    // in the URL would immediately reprocess it and reopen the view the user
    // just closed. `searchParams` is the actual "did a new arrival happen"
    // signal here (see the test harness comment on why it's a stable
    // instance) — subscription's role is only to seed `showOnlyWAPIPlans` /
    // `period` once, off primitives that don't churn on their own.
  }, [
    searchParams,
    setSearchParams,
    subscription?.status,
    subscription?.frequency,
    togglePlansView,
    setPeriod,
  ]);

  // Handle openLeadLimitAddon query param to open Lead Limit Addon modal
  useEffect(() => {
    const openLeadLimitAddonParam = searchParams.get('openLeadLimitAddon');
    if (openLeadLimitAddonParam === 'true') {
      // Ensure products are loaded before opening the modal
      if (
        workspaceInfo?.isActive &&
        (isAdvancedPlan || isBusinessPlan) &&
        !products
      ) {
        getProducts();
      }

      // Open the Lead Limit Addon modal
      toggleManageLeadLimitAddonModal(true);

      // Remove the query param after opening the modal
      const timeoutId = setTimeout(() => {
        searchParams.delete('openLeadLimitAddon');
        setSearchParams(searchParams, { replace: true });
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [
    searchParams,
    setSearchParams,
    toggleManageLeadLimitAddonModal,
    workspaceInfo?.isActive,
    products,
    isAdvancedPlan,
    isBusinessPlan,
    isGrowthPlan,
    getProducts,
  ]);

  /*
    BUG-861: opens the Leadbot Levels add-on modal straight from the reactivation
    gate in Automatizaciones -> Leadbot -> Tiempos. Without this the CTA only
    landed the customer on the Subscription tab and left them to find the add-on
    card themselves, which is the same dead end the gate exists to remove.
    Mirrors the openLeadLimitAddon handler above, including its products
    preload and param cleanup.
  */
  useEffect(() => {
    const openAdvancedLeadbotAddonParam = searchParams.get(
      OPEN_ADVANCED_LEADBOT_ADDON_PARAM,
    );
    if (openAdvancedLeadbotAddonParam === 'true') {
      // Ensure products are loaded before opening the modal
      if (
        workspaceInfo?.isActive &&
        (isAdvancedPlan || isBusinessPlan) &&
        !products
      ) {
        getProducts();
      }

      // Open the Advanced Leadbot add-on modal
      toggleManageAdvancedLeadbotModal(true);

      // Remove the query param after opening the modal
      const timeoutId = setTimeout(() => {
        searchParams.delete(OPEN_ADVANCED_LEADBOT_ADDON_PARAM);
        setSearchParams(searchParams, { replace: true });
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [
    searchParams,
    setSearchParams,
    toggleManageAdvancedLeadbotModal,
    workspaceInfo?.isActive,
    products,
    isAdvancedPlan,
    isBusinessPlan,
    isGrowthPlan,
    getProducts,
  ]);

  // RET-1244: getPlans() no longer decides currency client-side (the BFF resolves
  // it server-side and pins active workspaces to their subscription currency), so
  // the former subscription.currency gate — which existed only to avoid the old
  // geolocation fallback — is no longer needed.
  useEffect(() => {
    if (showPlansView && workspaceInfo?.isActive && !plans) {
      getPlans();
    }
  }, [showPlansView, workspaceInfo?.isActive, plans, getPlans]);

  // Allowed product types for purchase
  const ALLOWED_PRODUCTS_TO_SELL = [
    eProductType.LEADBOT_PREMIUM,
    eProductType.LEADBOT_LEVELS,
    eProductType.LEAD_LIMIT_ADDON,
  ];

  // This variable contains all the available products for purchase on the checkout view
  const availableProductsForPurchase =
    planToBuy?.relatedProducts?.filter(
      (product) =>
        product.productType !== eProductType.TEMPLATE_CREDITS_LIMIT &&
        ALLOWED_PRODUCTS_TO_SELL.includes(
          product.productType as eProductType,
        ) &&
        !selectedAddonProducts.some(
          (selectedProduct) => selectedProduct.uuid === product.uuid,
        ),
    ) || [];

  const queryParams = new URLSearchParams(location.search);
  const ctaType = queryParams.get('cta_type');

  // Handler functions for managing selected addon products
  function addSelectedAddonProduct(product: tRelatedProduct) {
    setSelectedAddonProducts((prevProducts) => {
      // Check if product is already selected
      const isAlreadySelected = prevProducts.some(
        (existingProduct) => existingProduct.uuid === product.uuid,
      );

      if (!isAlreadySelected) {
        return [...prevProducts, product];
      }

      return prevProducts;
    });
  }

  function removeSelectedAddonProduct(productUuid: string) {
    setSelectedAddonProducts((prevProducts) =>
      prevProducts.filter((product) => product.uuid !== productUuid),
    );
  }

  function toggleSelectedAddonProduct(product: tRelatedProduct) {
    const isSelected = selectedAddonProducts.some(
      (selectedProduct) => selectedProduct.uuid === product.uuid,
    );

    if (isSelected) {
      removeSelectedAddonProduct(product.uuid);
    } else {
      addSelectedAddonProduct(product);
    }
  }
  const watermarkRemovalDescription =
    (workspaceInfo?.isActive &&
      isLegacyPlan &&
      t('addOns.removeWatermark.descriptionLegacyPlan')) ||
    (workspaceInfo?.isActive && t('addOns.removeWatermark.description')) ||
    t('addOns.removeWatermark.descriptionForPlans');

  const advancedLeadbotDescription =
    (workspaceInfo?.isActive && t('addOns.advancedLeadbot.description')) ||
    t('addOns.advancedLeadbot.descriptionForPlans');

  //Addons that are available for purchase
  const addOns: tAddOn[] = [
    {
      includedInPlans: [
        ePlanCode.PLAN_BASIC,
        ePlanCode.PLAN_PROFESSIONAL,
        ePlanCode.PLAN_ENTERPRISE,
        ePlanCode.PLAN_LEGACY,
      ],
      title: t('addOns.addMoreUsers.title'),
      description: !workspaceInfo?.isActive
        ? t('addOns.addMoreUsers.descriptionForPlans')
        : t('addOns.addMoreUsers.description'),
      icon: UserPlusIcon,
      active: subscription.purchasedSeats > 0,
      price: getExtraSeatsPrice(),
      type: t('addOns.seat'),
    },
    {
      includedInPlans: [ePlanCode.PLAN_BASIC, ePlanCode.PLAN_LEGACY],
      title: t('addOns.removeWatermark.title'),
      description: watermarkRemovalDescription,
      icon: PhotoIcon,
      active: watermarkRemoval,
      price: getWatermarkPlanRemovalPrice(),
      type: '',
    },
    {
      includedInPlans: [ePlanCode.PLAN_BASIC],
      title: t('addOns.advancedLeadbot.title'),
      description: advancedLeadbotDescription,
      icon: LeadbotIcon,
      active: leadbotLevelsAccess,
      price: getAdvancedLeadbotPrice(),
      type: '',
    },
    {
      includedInPlans: [ePlanCode.PLAN_BASIC, ePlanCode.PLAN_ENTERPRISE],
      title: t('addOns.leadLimitAddon.title'),
      description: t('addOns.leadLimitAddon.description'),
      icon: ChatBubbleLeftRightIcon,
      active: isLeadLimitAddonActive(),
      price: getLeadLimitAddonPrice(),
      type: '',
    },
    {
      includedInPlans: [], // [eSubscription.GROWTH, eSubscription.ADVANCED],
      // Branches by flag like every other migrated string, even though the
      // empty `includedInPlans` makes this row unreachable today: the day
      // someone uncomments that list, a workspace without the wallet would
      // start reading "saldo" without anyone touching this line.
      title: t(`addOns.creditBag.title${isWalletEnabled ? '' : 'Legacy'}`),
      description: t('addOns.creditBag.description'),
      icon: ShoppingBagIcon,
      active: false,
      price: 28,
      type: t('addOns.seat'),
    },
    {
      includedInPlans: [], // [eSubscription.GROWTH],
      title: t('addOns.whatsappMetrics.title'),
      description: t('addOns.whatsappMetrics.description'),
      icon: ChartPieIcon,
      active: false,
      price: 99,
      type: '',
    },
    {
      includedInPlans: [], // [eSubscription.GROWTH, eSubscription.ADVANCED],
      title: t('addOns.weekendSupport.title'),
      description: t('addOns.weekendSupport.description'),
      icon: ChatBubbleLeftRightIcon,
      active: false,
      price: 100,
      type: '',
    },
    {
      includedInPlans: [] as ePlanCode[], // [eSubscription.ADVANCED],
      title: t('addOns.newWhatsappNumber.title'),
      description: t('addOns.newWhatsappNumber.description'),
      icon: whatsAppLogoPurple,
      active: false,
      price: 100,
      type: '',
    },
  ];

  // Filter addons based on products.allRelatedProducts
  const filteredAddOns = (() => {
    // If products are not loaded, fall back to original filtering logic
    if (
      !products?.allRelatedProducts ||
      products.allRelatedProducts.length === 0
    ) {
      return addOns.filter((addOn) =>
        addOn.includedInPlans.includes(
          subscription.planCode ?? ePlanCode.PLAN_LEGACY,
        ),
      );
    }

    return addOns.filter((addOn) => {
      const isUserAddOn = addOn.title === t('addOns.addMoreUsers.title');
      const isWatermarkRemovalAddOn =
        addOn.title === t('addOns.removeWatermark.title');
      const isAdvancedLeadbotAddOn =
        addOn.title === t('addOns.advancedLeadbot.title');
      const isLeadLimitAddon = addOn.title === t('addOns.leadLimitAddon.title');

      // For addons that map to product types, check if they exist in allRelatedProducts
      if (isUserAddOn) {
        // RET-2032: routed through the same resolver as the modal's price
        // lookup and `updateSeatsPlan`'s purchase target (`findCatalogProduct`
        // / `hasCatalogProduct` in SubscriptionManager.functions.tsx) instead
        // of a standalone case-sensitive check against only
        // `allRelatedProducts`. A lowercase `productType` used to hide this
        // card even though the modal's own lookup would have matched it
        // case-insensitively — and a SEATS entry that only lived in
        // `availableForPurchase` was invisible to this gate entirely.
        return hasCatalogProduct(products, eProductType.SEATS);
      }
      if (isWatermarkRemovalAddOn) {
        return products.allRelatedProducts.some(
          (product) => product.productType === eProductType.LEADBOT_PREMIUM,
        );
      }
      if (isAdvancedLeadbotAddOn) {
        return products.allRelatedProducts.some(
          (product) => product.productType === eProductType.LEADBOT_LEVELS,
        );
      }
      if (isLeadLimitAddon) {
        return products.allRelatedProducts.some(
          (product) => product.productType === eProductType.LEAD_LIMIT_ADDON,
        );
      }

      // For other addons (credit bag, whatsapp metrics, etc.), keep original logic
      return addOn.includedInPlans.includes(
        subscription.planCode ?? ePlanCode.PLAN_LEGACY,
      );
    });
  })();

  const subscriptionPageTrackedForWorkspaceRef = useRef<string | undefined>(
    undefined,
  );
  useEffect(() => {
    if (currentWorkspace?.id === undefined) {
      return;
    }
    const preferred = preferredPaymentMethodRef.current;
    retrievePaymentMethods(
      preferred?.workspaceId === currentWorkspace.id
        ? preferred.paymentMethodId
        : undefined,
    );
    if (
      workspaceInfo?.isActive &&
      (isAdvancedPlan || isBusinessPlan || isGrowthPlan)
    ) {
      getProducts();
    }

    // Once per workspace visit: the effect deps thrash while subscription data
    // loads (plan booleans flip undefined→false→true and on every refetch),
    // which made this event fire ~250×/user/mo (2.5M events/mo of noise).
    if (
      !isClient &&
      subscriptionPageTrackedForWorkspaceRef.current !== currentWorkspace.id
    ) {
      subscriptionPageTrackedForWorkspaceRef.current = currentWorkspace.id;
      trackEvent('front.settings.page.subscription', getBootProperties());
    }
  }, [
    workspaceInfo?.isActive,
    isAdvancedPlan,
    isBusinessPlan,
    isGrowthPlan,
    currentWorkspace?.id,
  ]);

  // RET-1974: opening or closing the checkout starts the preferred card over.
  useEffect(() => {
    preferredPaymentMethodRef.current = undefined;
  }, [showCheckoutView]);

  // Reset selected addon products when plan changes
  useEffect(() => {
    setSelectedAddonProducts([]);
  }, [planToBuy?.uuid]);

  // Reset all states when workspace changes
  useEffect(() => {
    if (currentWorkspace?.id === undefined) {
      return;
    }

    // Don't reset plans view if upgrade query param is present or was recently processed
    const upgradeParam = searchParams.get('upgrade');
    const shouldKeepPlansView =
      upgradeParam === 'true' || upgradeProcessedRef.current;

    // Reset all modal states
    toggleAddPaymentMethodModal(false);
    togglePaymentCompletedModal(false);
    togglePaymentMethodsManager(false);
    toggleCheckoutView(false);
    if (!shouldKeepPlansView) {
      togglePlansView(false);
    }
    toggleTrialCheckoutView(false);
    toggleManageUsersModal(false);
    toggleManageWatermarkModal(false);
    toggleManageAdvancedLeadbotModal(false);
    toggleManageLeadLimitAddonModal(false);
    toggleCancelSubscriptionModal(false);

    // Reset all form states
    setIsTrial(false);
    setExtraSeats(0);
    setMonthlyCredits(null);
    setPlanToBuy(null);
    setSelectedAddonProducts([]);
    setInvoicePreview(null);

    // Reset all loading states
    toggleUpdatingPlan(false);
    setAddingPaymentMethod(false);
    toggleProcessingPayment(false);
    togglePaymentMethodRequest(false);
    toggleCancellingSubscription(false);
    setLoadingInvoicePreview(false);

    // Reset payment method states
    preferredPaymentMethodRef.current = undefined;
    setSelectedPaymentMethod(INITIAL_PAYMENT_METHOD);
    setCurrentPaymentMethods([]);
    setPaymentMethodsLoadState(
      SUBSCRIPTION_MANAGER_INITIAL_STATE.paymentMethodsLoadState,
    );
    setPaymentMethodDefaults({});
    setTrialChargePaymentMethodId(
      SUBSCRIPTION_MANAGER_INITIAL_STATE.trialChargePaymentMethodId,
    );
    setPaymentMethodsOptions([]);

    // Reset period to default (unless upgrade query param is present)
    if (!shouldKeepPlansView) {
      setPeriod(ePlanFrequency.MONTHLY);
    }

    // Reset products
    setProducts(null);

    // Reset cancellation modal states
    resetCancellationModalStates();
  }, [currentWorkspace?.id]);

  const CancelSubscriptionValues: tCancelForm = {
    cancellationReason: '',
    description: '',
  };

  const cancelSubscriptionFormikConfig: FormikConfig<tCancelForm> = {
    initialValues: CancelSubscriptionValues,
    validationSchema: CancelSubscriptionSchema(t),
    onSubmit: cancelSubscription,
  };

  const cancelSubscriptionFormik = useFormik(cancelSubscriptionFormikConfig);

  function resetCancellationModalStates() {
    toggleCancelSubscriptionModal(false);
    toggleCancellingSubscription(false);
    if (cancelSubscriptionFormik) {
      cancelSubscriptionFormik.resetForm();
    }
  }

  /**
   * The seat price shown on the add-ons CARD.
   *
   * Two different questions live here, and they are answered by different sources:
   *
   *  - A workspace with no live subscription is being shown the CATALOGUE ("what would
   *    a seat cost if you subscribed?"), which comes from the plans list.
   *  - A workspace that can actually buy gets the real quote, which since U1 resolves
   *    through `resolveSeatQuote` — the same path the Team tab's add-teammate flow and
   *    the ManageUsers modal read, so no two surfaces in Settings can quote different
   *    numbers or disagree about whether a price exists.
   *
   * Note the eligibility test is the SUBSCRIPTION's status, not `workspaceInfo.isActive`
   * — that flag is workspace-enabled state, and a churned workspace (flag on, no
   * subscription) must be shown the catalogue rather than a quote it cannot honour.
   */
  function getExtraSeatsPrice() {
    const quotedPrice = resolveSeatPriceCents({
      status: subscription.status,
      planCode: subscription.planCode,
      currency: subscription.currency,
      purchasedSeatsCost: subscription.purchasedSeatsCost,
      allRelatedProducts: products?.allRelatedProducts,
      availableForPurchase: products?.availableForPurchase,
    });

    if (quotedPrice !== null) {
      return quotedPrice;
    }

    // No quote: fall back to the catalogue figure for this plan. The card is a display
    // surface, so an unknown price renders as absent (0) here rather than blocking —
    // the surfaces that actually take money read the quote itself and refuse instead.
    return (
      plans?.[period].find((plan) => plan.planCode === ePlanCode.PLAN_BASIC)
        ?.extraSeatPrice || 0
    );
  }

  function getWatermarkPlanRemovalPrice() {
    if (!workspaceInfo?.isActive) {
      return (
        plans?.[period]
          .find((plan) => plan.planCode === ePlanCode.PLAN_BASIC)
          ?.relatedProducts.find(
            (product) => product.productType === eProductType.LEADBOT_PREMIUM,
          )?.price || 0
      );
    }
    if (isLegacyPlan) {
      return subscription.purchasedSeatsCost;
    }

    const watermarkPlanRemovalPrice = products?.allRelatedProducts?.find(
      (product) => product.productType === eProductType.LEADBOT_PREMIUM,
    )?.price;

    return watermarkPlanRemovalPrice ? watermarkPlanRemovalPrice : 0;
  }

  function getAdvancedLeadbotPrice() {
    if (!workspaceInfo?.isActive) {
      return (
        plans?.[period]
          .find((plan) => plan.planCode === ePlanCode.PLAN_BASIC)
          ?.relatedProducts.find(
            (product) => product.productType === eProductType.LEADBOT_LEVELS,
          )?.price || 0
      );
    }

    const advancedLeadbotPrice = products?.allRelatedProducts?.find(
      (product) => product.productType === eProductType.LEADBOT_LEVELS,
    )?.price;

    return advancedLeadbotPrice ? advancedLeadbotPrice : 0;
  }

  function getLeadLimitAddonPrice() {
    if (!workspaceInfo?.isActive) {
      return (
        plans?.[period]
          .find(
            (plan) =>
              plan.planCode === ePlanCode.PLAN_BASIC ||
              plan.planCode === ePlanCode.PLAN_PROFESSIONAL,
          )
          ?.relatedProducts.find(
            (product) => product.productType === eProductType.LEAD_LIMIT_ADDON,
          )?.price || 0
      );
    }

    const leadLimitAddonPrice = products?.allRelatedProducts?.find(
      (product) => product.productType === eProductType.LEAD_LIMIT_ADDON,
    )?.price;

    return leadLimitAddonPrice ? leadLimitAddonPrice : 0;
  }

  function isLeadLimitAddonActive() {
    if (!workspaceInfo?.isActive || !products) {
      return false;
    }

    const leadLimitAddonProduct = products.allRelatedProducts?.find(
      (p) => p.productType === eProductType.LEAD_LIMIT_ADDON,
    );

    if (!leadLimitAddonProduct) {
      return false;
    }

    return products.purchasedProducts.some(
      (product) => product.uuid === leadLimitAddonProduct.uuid,
    );
  }

  function checkoutSelectedPlan(selectedPlan?: tPlan) {
    if (!selectedPlan) {
      return;
    }

    // Event #1: Track when non-paying user selects a plan (Buy now/Try for 14 days)
    // This event should NOT fire for plan changes (upgrades/downgrades)
    if (!workspaceInfo?.isActive && currentUser && currentWorkspace?.id) {
      const planAmount = Math.round(selectedPlan.totalPrice).toString();

      trackAddToCartEvent({
        event: 'AddToCart',
        email: currentUser.email ?? '',
        name: currentUser.name ?? '',
        phone: currentUser.phoneNumber?.toString() ?? '',
        date: new Date().toISOString(),
        amount: planAmount,
        currency: selectedPlan.currency.toLowerCase(),
        recurrencyType: selectedPlan.frequencyName,
        plan: getPlanName(selectedPlan.planCode),
        workspaceId: currentWorkspace.id,
        isTrial: isTrial,
      });
    }

    // Calculate seat preservation for plan changes
    const currentIncludedSeats = subscription.includedSeats;
    const newIncludedSeats = selectedPlan.includedSeats;
    const currentTotalSeats =
      currentIncludedSeats + subscription.purchasedSeats;

    // If upgrading to a plan with more included seats, adjust extra seats
    if (newIncludedSeats > currentIncludedSeats) {
      const seatIncrease = newIncludedSeats - currentIncludedSeats;
      const adjustedExtraSeats = Math.max(
        0,
        subscription.purchasedSeats - seatIncrease,
      );
      setExtraSeats(adjustedExtraSeats);
    } else if (newIncludedSeats < currentIncludedSeats) {
      // For downgrades, preserve total seat count by adjusting extra seats
      const adjustedExtraSeats = Math.max(
        0,
        currentTotalSeats - newIncludedSeats,
      );
      setExtraSeats(adjustedExtraSeats);
    } else {
      // For same seat count, preserve current extra seats
      setExtraSeats(subscription.purchasedSeats);
    }

    previousPaymentMethodsRef.current = {
      plan: paymentMethodDefaults.planPaymentMethodId ?? null,
      account: paymentMethodDefaults.customerDefaultPaymentMethodId ?? null,
    };

    setPlanToBuy(selectedPlan);
    toggleCheckoutView(true);
  }

  function editPaymentMethods() {
    toggleCheckoutView(false);
    toggleTrialCheckoutView(false);
    togglePaymentMethodsManager(true);
  }

  // RET-2028: reference-stable (useCallback, empty deps) so it is safe to sit
  // in returnToOverview's dependency array below. The body only touches a
  // ref and useState setters — both stable across renders per React's
  // guarantees — so an empty array is correct, not an omission.
  const removeCoupon = useCallback(() => {
    // RET-1157 / JFR-004: abort any in-flight validation so its resolution
    // cannot race the reset and re-populate state we just cleared.
    couponAbortRef.current?.abort();
    couponAbortRef.current = null;
    setCouponCode('');
    setAppliedCouponDiscount(null);
    setCouponDiscountType(null);
    setCouponDuration(null);
    setCouponDurationInMonths(null);
    setCouponError(null);
  }, []);

  // RET-1285 / RET-2028: rotate the purchase-intent idempotency key. Pulled
  // out of returnFromCheckout so returnToOverview can share it too (see
  // below) instead of returnFromCheckout calling returnToOverview outright —
  // the two mean different things (returnFromCheckout backs out of checkout
  // but deliberately leaves the plans view open so the customer can pick a
  // different plan; returnToOverview closes the plans view as well), so
  // delegating one to the other would fold that difference away. Reference-
  // stable (useCallback, empty deps) because it only mutates a ref — a ref
  // write needs no dependency, so this is exactly as stable as inlining the
  // assignment — which keeps it safe inside returnToOverview's own deps
  // array below.
  const resetPurchaseIntent = useCallback(() => {
    purchaseIntentIdRef.current = '';
  }, []);

  // RET-2028: reference-stable (useCallback) so SubscriptionManager
  // .component.tsx's single unmount effect can reuse this exact policy —
  // close the payment methods manager, the checkout, and the plans view, and
  // clear any in-flight coupon — instead of standing up a second,
  // easy-to-drift reset mechanism. `removeCoupon`, `resetPurchaseIntent`, and
  // `togglePlansView` are themselves stable useCallbacks, so this one is too:
  // a non-stable function here would re-subscribe that unmount effect on
  // every render and fire its cleanup while the customer is still mid-flow.
  //
  // RET-1285 / RET-2028: also rotates the purchase intent. Before phase 2,
  // returnToOverview only ever ran AFTER a purchase settled (success, or the
  // PaymentMethodsManager back button) — returnFromCheckout owned every
  // abandon path, so it was the only place that needed to rotate the key.
  // Phase 2 added a fourth caller — the unmount cleanup above — that closes
  // an in-flight checkout WITHOUT going through returnFromCheckout. Left
  // unrotated, a customer who abandons a checkout by navigating away and
  // later comes back to deliberately buy again would send that second,
  // distinct purchase under the first attempt's key, and the backend's
  // idempotency dedup would treat it as a retry of the first — silently
  // dropping a legitimate charge. Rotating here makes "the purchase attempt
  // is over" the same fact everywhere returnToOverview is already called.
  const returnToOverview = useCallback(() => {
    resetPurchaseIntent();
    removeCoupon();
    togglePaymentMethodsManager(false);
    toggleCheckoutView(false);
    toggleTrialCheckoutView(false);
    togglePlansView(false);
  }, [removeCoupon, resetPurchaseIntent, togglePlansView]);

  // RET-1285: return the current purchase intent, generating a fresh one the
  // first time it is requested for an attempt. Callers capture the result into a
  // local const before any await so the post-3DS re-call reuses the same value.
  function ensurePurchaseIntentId(): string {
    if (!purchaseIntentIdRef.current) {
      purchaseIntentIdRef.current = crypto.randomUUID();
    }
    return purchaseIntentIdRef.current;
  }

  // RET-1285 / RET-1372: map a failed purchase to its toast message via the
  // shared billing-error resolver. It keeps the in-flight duplicate (409
  // `purchase_in_progress`) calming message, resolves specific decline copy,
  // and — unlike the old `errors.${message}` — falls back to an actionable
  // bank-decline or generic message instead of leaking a raw Stripe code.
  function resolvePurchaseErrorMessage(
    error: (BillingError & { error_code?: string }) | undefined,
  ): string {
    // BillingError.message is typed as eSubscriptionCreationStatus, but the wire
    // value for this path is the raw error code string — the cast bridges that
    // declared-vs-actual mismatch for the resolver's structured-payload shape.
    return resolveBillingErrorMessage(
      error as unknown as tBillingErrorPayload,
      t,
    );
  }

  // Telemetry companion for the resolver. `front.subscription.button.pay_plan`
  // carried `payment_status: 'failed'` and nothing else, so 473 failed payments
  // over 90 days were unattributable — we could count them but never say WHY a
  // card was refused. Reporting whether the customer got specific copy (rather
  // than one of the two catch-alls) measures the residual gap directly, so a
  // decline code that still has no copy shows up as a number instead of a
  // support ticket.
  function isSpecificDeclineCopy(message: string): boolean {
    return (
      message !== t('errors.generic') &&
      message !== t('errors.bank_decline_generic')
    );
  }

  function returnFromCheckout() {
    // RET-1285 / RET-2028: rotate the purchase intent when the flow closes so
    // a deliberate second purchase (after getSummary refreshes the data) is a
    // new intent. Shared with returnToOverview via resetPurchaseIntent (see
    // its definition above) rather than inlined here twice.
    resetPurchaseIntent();
    // RET-1157 / F3: reset coupon state when the user backs out of checkout
    // (mirrors returnToOverview). Otherwise a coupon applied for Plan A would
    // silently re-apply when the user picks Plan B and re-enters checkout —
    // including being sent as `coupon_code` in the create-subscription payload.
    removeCoupon();
    toggleCheckoutView(false);
    toggleTrialCheckoutView(false);
    togglePaymentMethodsManager(false);
    toggleAddPaymentMethodModal(false);
    toggleManageWatermarkModal(false);
    toggleManageAdvancedLeadbotModal(false);
    toggleManageLeadLimitAddonModal(false);
    toggleManageUsersModal(false);
    setExtraSeats(0);
    setSelectedAddonProducts([]);
    setIsTrial(false);
  }

  async function getProducts() {
    try {
      const products = await SubscriptionManagerService.getProducts(
        currentWorkspace?.id ?? '',
      );
      setProducts(products);
    } catch (err) {
      const error = err as BillingError;
      addAlert({
        type: eAlertType.ERROR,
        message: `${error.errors[0]}`,
      });
    }
  }

  async function reactivateScheduledCancellation() {
    if (!currentWorkspace?.id || reactivatingSubscription) {
      return;
    }
    toggleReactivatingSubscription(true);
    try {
      await SubscriptionManagerService.reactivateSubscription(
        currentWorkspace.id,
      );
      await getSummary();
      addAlert({
        type: eAlertType.SUCCESS,
        message: t('messages.subscriptionReactivated'),
      });
    } catch (error) {
      addAlert({
        type: eAlertType.ERROR,
        message: `${t('messages.errorReactivatingSubscription')} ${error}`,
      });
    } finally {
      toggleReactivatingSubscription(false);
    }
  }

  function reactivatePlan() {
    // A subscription that is still active but pending cancellation only needs
    // the scheduled cancellation reverted, so reactivate it in place. A fully
    // cancelled subscription has no active period to restore and must go
    // through plan selection again.
    if (shouldReactivateInPlace(subscription)) {
      reactivateScheduledCancellation();
      return;
    }
    setShowOnlyWAPIPlans(true);
    setPeriod(ePlanFrequency.MONTHLY);
    togglePlansView(true);
  }
  async function addPaymentMethod() {
    if (
      currentWorkspace?.id === undefined ||
      addingPaymentMethod ||
      redirectReturnPendingRef.current ||
      isEmpty(stripe) ||
      isEmpty(elements)
    ) {
      return;
    }
    setAddingPaymentMethod(true);
    // RET-2426: read once, before any await. Closing the modal drops the
    // options from state, and this save must keep what its caller asked for.
    const callerOptions = addPaymentMethodOptions;
    // Flip to true only after the full happy-path lands. Any early return
    // (Stripe submit error, confirmSetup error) leaves this false so the
    // GRO-811 auto-return below is gated by an actual success.
    let addPaymentMethodSucceeded = false;
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        LLog.info(
          {
            component: LOG_COMPONENTS.UI,
            method: 'SubscriptionManager.hook',
            operation: LOG_OPERATIONS.UI_INTERACTION,
          },
          `SubmitError:${submitError}`,
        );
        trackSubscriptionEvent(SUBSCRIPTION_EVENTS.PAYMENT_METHOD_ADD, {
          display_name: 'Agregar metodo de pago',
          description: 'Intento fallido de agregar metodo de pago',
          payment_status: 'failed',
        });
        // D4 (RET-1923): this branch used to log, track, stop the spinner and
        // `return` — nothing reached the screen, so it read as if nothing had
        // happened at all. The modal is deliberately left open here (unlike
        // the confirmSetup error branch below) so the customer can fix
        // whatever `elements.submit()` rejected and try again in place.
        //
        // D4 copy (checker, vuelta 2): this used to reuse the generic
        // "payment processing" alert key — the SAME key the real plan-charge
        // failure below (~L1989) uses. Nothing was charged here — this is a
        // SetupIntent form (adding a card), rejected by Stripe.js field
        // validation before any request left the browser — so that copy was
        // simply false, and it duplicated what the PaymentElement already
        // renders inline right below the form. `paymentMethodDataError` is
        // a key of its own so a future change to the real charge-failure
        // copy can't silently change this one too, and it names the actual
        // problem (the card data) instead of inventing a charge attempt.
        addAlert({
          type: eAlertType.ERROR,
          message: t('messages.paymentMethodDataError'),
        });
        setAddingPaymentMethod(false);
        return;
      }

      // Create the PaymentMethod using the details collected by the Payment Element

      const clientSecret =
        await SubscriptionManagerService.attachPaymentMethodToCustomer(
          currentWorkspace.id,
        );

      // RET-1974: a bank that authenticates on its own page sends the customer
      // back here. To this page's origin, not `VITE_BASE_URL_FRONTEND`: in dev
      // and staging that names a host that does not resolve, and the snapshot
      // below lives in this origin's `sessionStorage`. To the Subscription
      // section, where the card ends up selected. An add that must not become
      // the default says so in the URL, since its options die with the page.
      const returnUrl = `${window.location.origin}${
        RouterPaths.settings
      }?section=${SettingsTabs.SUBSCRIPTION}${
        callerOptions?.skipDefault ? '&skip_default=1' : ''
      }`;

      LLog.info(
        {
          component: LOG_COMPONENTS.UI,
          method: 'SubscriptionManager.hook',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Info: returnURL loaded: ' + returnUrl,
      );

      // RET-1974 (`M-21`): if the bank takes the customer to its own page,
      // the cards from before this add are lost with everything in memory,
      // and the debt payment must be able to send them back (RET-2430). They
      // are kept until the return; an add that resolves here drops them.
      const setupIntentId = setupIntentIdFromClientSecret(clientSecret.data);
      writeRedirectReturnSnapshot(currentWorkspace.id, {
        setupIntentId,
        previousPaymentMethods: showCheckoutView
          ? { ...previousPaymentMethodsRef.current }
          : {
              plan: paymentMethodDefaults.planPaymentMethodId ?? null,
              account:
                paymentMethodDefaults.customerDefaultPaymentMethodId ?? null,
            },
      });

      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret: clientSecret.data,
        confirmParams: {
          return_url: returnUrl,
        },
        redirect: 'if_required' as const,
      });
      clearRedirectReturnSnapshot(currentWorkspace.id, setupIntentId);

      if (error) {
        // This point is only reached if there's an immediate error when
        // creating the PaymentMethod. Show the error to your customer (for example, payment details incomplete)
        LLog.error(
          {
            component: LOG_COMPONENTS.UI,
            method: 'SubscriptionManager.hook',
            operation: LOG_OPERATIONS.ERROR,
          },
          'Error in SubscriptionManager.hook',
          error as unknown as Error,
        );
        // `loadStripe` is called without a `locale`, so Stripe.js writes this
        // message in the BROWSER's language, which is not necessarily the
        // language the UI is rendered in: an English-locale browser gets an
        // English sentence inside the Spanish app. Resolve our own copy from
        // the error code instead, exactly like the confirmPayment sites below.
        const declineMessage = resolveBillingErrorMessage(
          stripeJsErrorToBillingPayload(error),
          t,
        );
        addAlert({
          type: eAlertType.ERROR,
          message: declineMessage,
        });
        trackSubscriptionEvent(SUBSCRIPTION_EVENTS.PAYMENT_METHOD_ADD, {
          display_name: 'Agregar metodo de pago',
          description: 'Intento fallido de agregar metodo de pago',
          payment_status: 'failed',
          decline_code: error.decline_code ?? error.code ?? '',
          decline_copy_resolved: isSpecificDeclineCopy(declineMessage),
        });
        setAddingPaymentMethod(false);
        // Closing the modal discarded the whole form, so a customer who
        // mistyped three digits had to retype the entire card - which is how
        // one workspace ended up adding three cards in eleven minutes. A card
        // or validation error is correctable in place, so keep the form (and
        // everything already typed) up and let the alert say what to fix.
        // Anything the form cannot fix still closes, as before.
        if (!isStripeErrorCorrectableInForm(error)) {
          toggleAddPaymentMethodModal(false);
        }
        return;
      }

      const tailOutcome = await finishConfirmedCardAdd({
        workspaceId: currentWorkspace.id,
        setupIntent,
        skipDefault: callerOptions?.skipDefault ?? false,
        onIntentSucceeded: (paymentMethodId) => {
          // The `requestId` lets only the caller that opened this add act on it.
          if (!callerOptions?.requestId) return;
          const { requestId } = callerOptions;
          setCardSavedForCaller((current) => ({
            token: current.token + 1,
            requestId,
            paymentMethodId,
          }));
        },
        onAddFormDone: ({ keepOpen }) => {
          setAddingPaymentMethod(false);
          if (!keepOpen) toggleAddPaymentMethodModal(false);
        },
      });
      if (tailOutcome !== 'saved') return;

      addPaymentMethodSucceeded = true;
      trackSubscriptionEvent(SUBSCRIPTION_EVENTS.PAYMENT_METHOD_ADD, {
        display_name: 'Agregar metodo de pago',
        description: 'Agrega un metodo de pago exitoso',
        payment_status: 'success',
      });
    } catch (err) {
      const error = err as BillingError;

      addAlert({
        type: eAlertType.ERROR,
        message: t(`errors.${error?.message ?? ''}`),
      });
      trackSubscriptionEvent(SUBSCRIPTION_EVENTS.PAYMENT_METHOD_ADD, {
        display_name: 'Agregar metodo de pago',
        description: 'Intento fallido de agregar metodo de pago',
        payment_status: 'failed',
      });
    }
    setAddingPaymentMethod(false);
    toggleAddPaymentMethodModal(false);

    // GRO-811 — if the user landed here from the credits purchase modal
    // (`?returnTo=credits_purchase`), bring them back to WhatsApp API on
    // success so the in-flight purchase flow can reopen with the preserved
    // draft. Failure paths skip the redirect — the user stays in Settings
    // where the alert is visible.
    //
    // RET-2426: an add opened by a caller with options belongs to that caller,
    // which stays where it is.
    if (
      addPaymentMethodSucceeded &&
      callerOptions === null &&
      searchParams.get('returnTo') === 'credits_purchase'
    ) {
      navigate(`${RouterPaths.wapi}?returnFrom=credits_purchase`);
    }
  }

  /**
   * RET-1974: what an add does once Stripe has answered for its SetupIntent:
   * the status filter, the promotion to default with its refusals, and the
   * list re-read that selects the card. The inline add and the return from a
   * bank redirect both end here, so the two cannot drift apart. The inline
   * add's own effects run through the callbacks, at the point they ran before.
   */
  async function finishConfirmedCardAdd({
    workspaceId,
    setupIntent,
    skipDefault,
    onIntentSucceeded,
    onAddFormDone,
  }: tFinishConfirmedCardAddOptions): Promise<tFinishConfirmedCardAddOutcome> {
    // RET-2155: `confirmSetup` coming back without `error` is not the bank
    // saying yes. With `redirect: 'if_required'` it also resolves for an
    // intent that is still `processing` or `requires_action`, and the card
    // of that intent used to be promoted like any other: the default became
    // a card nobody had confirmed. Only a SetupIntent that succeeded is
    // promoted. Anything else leaves the default where it was and is not an
    // add: no success notice, no success tracking, no GRO-811 return. The
    // form closes, because typing the same card again would only open a
    // second intent for it.
    if (setupIntent?.status !== 'succeeded') {
      addAlert({
        type: eAlertType.WARNING,
        message: t('paymentMethodNotices.notConfirmedYet'),
      });
      trackSubscriptionEvent(SUBSCRIPTION_EVENTS.PAYMENT_METHOD_ADD, {
        display_name: 'Agregar metodo de pago',
        description: 'Intento fallido de agregar metodo de pago',
        payment_status: 'failed',
        decline_code: `setup_intent_${setupIntent?.status ?? 'missing'}`,
      });
      onAddFormDone({ keepOpen: false });
      return 'stopped';
    }

    const addedPaymentMethodId =
      extractSetupIntentPaymentMethodId(setupIntent);

    // RET-2426: the caller that opened this add hears about the saved card
    // right away, before the list re-read below, because it charges the
    // card now («Cambiar tarjeta» of a paused low-balance top-up).
    if (addedPaymentMethodId) onIntentSucceeded(addedPaymentMethodId);

    // RET-2428: whether billing kept the card without making it the default.
    let savedWithoutDefault = false;
    // RET-2452 (`tarjeta-guardada`): with the past-due zone on screen, the
    // saved card is announced once the re-read below shows it, as
    // `pastDuePayment.cardSaved`, and it becomes the zone's selection. Holds
    // the notice it replaces, raised instead when the card cannot be named.
    let zoneCardSavedFallback: {
      type: eAlertType;
      titleKey: string;
      messageKey: string;
    } | null = null;

    // Attaching a card does not make it the provider default, and a card that
    // failed a charge stays the default. Promote the card the user just added
    // so the retry — and every later off-session charge — uses it instead of
    // the one that failed.
    //
    // RET-2426: not when the caller asked to skip it. «Cambiar tarjeta» asks
    // for the default itself, and only once the charge with this card is
    // confirmed: a declined card must never become the default (RC-21,
    // MO-8). Nothing here may announce a default that was not asked for, so
    // the success notice and the default tokens stay put too.
    if (addedPaymentMethodId) {
      if (!skipDefault) {
        try {
          // RET-2428: the 200 now says what billing did with the card. In a
          // trial the customer has not confirmed it is saved WITHOUT becoming
          // the default (`T-2`), and nothing below may say it did.
          const promotionOutcome = resolvePaymentMethodDefaultOutcome(
            await SubscriptionManagerService.makePaymentMethodDefault(
              addedPaymentMethodId,
              workspaceId,
            ),
            isTrialSubscription,
          );
          // D1/D2 (RET-1923): the happy path used to end in silence — no
          // confirmation that the card was saved, and no mention of the
          // heavier consequence: this card is now the account's default, so
          // it also governs plan renewal. This is the positive mirror of the
          // WARNING in the `catch` below, which already announces that same
          // consequence when the promotion fails.
          const successNotice = resolveAddPaymentMethodSuccessNotice({
            defaultCallSucceeded: true,
            pastDue: isSubscriptionPastDue(subscription.status),
            outcome: promotionOutcome,
          });
          if (
            successNotice &&
            isPastDueZoneActive &&
            promotionOutcome !== 'savedWithoutDefault'
          ) {
            zoneCardSavedFallback = successNotice;
          } else if (successNotice) {
            addAlert({
              type: successNotice.type,
              // RET-2348: with no title the toast paints the generic SUCCESS
              // one, «Operación exitosa». Raised from the wallet's top-up
              // modal after a declined charge, that read as the payment going
              // through. The notice now names what succeeded: the card.
              title: t(successNotice.titleKey),
              message: t(successNotice.messageKey),
            });
          }
          // RET-2348: tells whoever is listening that a card was saved AND is
          // now the default. The wallet's top-up modal is the listener: it
          // charges the default card, so this is the moment its screen has to
          // say that the card changed and the top-up is still not charged.
          // Raised here, inside the `try`, on purpose: a failed promotion
          // lands in the `catch` below and must NOT move it — the card the
          // wallet would charge is still the old one.
          //
          // RET-2428: a card saved WITHOUT becoming the default moves the
          // other signal instead. `cardSavedAsDefaultToken` keeps meaning
          // "saved AND default"; the top-up modal listens to both, because in
          // either case the card was saved and the top-up is still not
          // charged.
          if (promotionOutcome === 'savedWithoutDefault') {
            savedWithoutDefault = true;
            // The id first, in the same batch: whoever sees the token move
            // reads the card it belongs to (RC-16, the top-up charges it).
            setCardSavedWithoutDefaultPaymentMethodId(addedPaymentMethodId);
            setCardSavedWithoutDefaultToken((token) => token + 1);
          } else {
            setCardSavedAsDefaultPaymentMethodId(addedPaymentMethodId);
            setCardSavedAsDefaultToken((token) => token + 1);
          }
        } catch (defaultError) {
          // A refusal billing gave a reason for is not the "saved, but not the
          // default" the WARNING below announces. Either way the alert says
          // which one it was, and the add counts as FAILED: no success
          // tracking, and no GRO-811 return to the purchase.
          if (defaultError instanceof PaymentMethodDefaultRefusedError) {
            const { refusal } = defaultError;
            addAlert(resolvePaymentMethodDefaultRefusalNotice(refusal, t));
            // RET-2155: the bank refused the card when billing set it on the
            // subscription. Billing puts the previous card back, and says so
            // in `previousCardRestored`; the notice above only states that
            // the default did not change when that is true. The card stays
            // saved, so it is not deleted, and the form closes: typing it
            // again would get the same answer from the same bank. The list is
            // read again so the saved card shows in it, without asking for it
            // to be selected: the selection follows the default.
            if (refusal.status === 'paymentMethodDeclined') {
              trackSubscriptionEvent(SUBSCRIPTION_EVENTS.PAYMENT_METHOD_ADD, {
                display_name: 'Agregar metodo de pago',
                description: 'Intento fallido de agregar metodo de pago',
                payment_status: 'failed',
                decline_code: refusal.declineCode ?? refusal.status,
              });
              onAddFormDone({ keepOpen: false });
              preferredPaymentMethodRef.current = undefined;
              await retrievePaymentMethods();
              return 'stopped';
            }
            // RET-2347: the card's brand cannot be charged in the customer's
            // currency (American Express in USD on our Stripe account). This
            // card cannot pay for anything here, so keeping it would leave on
            // file a card that fails every charge: try to take it back off the
            // account.
            try {
              await SubscriptionManagerService.deletePaymentMethod(
                addedPaymentMethodId,
                workspaceId,
              );
            } catch (deleteError) {
              // Best effort, and billing refuses it for a customer's only
              // card, which is why the alert never says the card was not
              // saved. It already says what to do, and a second error about a
              // card the customer is not going to use would only bury it.
              LLog.error(
                {
                  component: LOG_COMPONENTS.SUBSCRIPTION,
                  method: 'SubscriptionManager.hook',
                  operation: LOG_OPERATIONS.ERROR,
                },
                'Error deleting the payment method billing refused to charge',
                deleteError instanceof Error
                  ? deleteError
                  : new Error(String(deleteError)),
              );
            }
            trackSubscriptionEvent(SUBSCRIPTION_EVENTS.PAYMENT_METHOD_ADD, {
              display_name: 'Agregar metodo de pago',
              description: 'Intento fallido de agregar metodo de pago',
              payment_status: 'failed',
              decline_code: refusal.status,
            });
            // The form stays open, as it does for a card Stripe rejected: the
            // way out is another card, and this is where it gets typed.
            onAddFormDone({ keepOpen: true });
            return 'stopped';
          }
          // Does not block the add: the card is attached and the selection below
          // still points at it, and every charge on this screen sends the card
          // explicitly, so the customer can pay right now. What does NOT follow
          // is the renewal — those are off-session and read the provider
          // default, which is still the old card. Saying nothing here would
          // leave the customer believing they changed the card for good, so the
          // failure has to reach the screen and not only the log.
          LLog.error(
            {
              component: LOG_COMPONENTS.SUBSCRIPTION,
              method: 'SubscriptionManager.hook',
              operation: LOG_OPERATIONS.ERROR,
            },
            'Error setting the newly added payment method as default',
            // The service rejects with a template string, not an Error, and
            // LLog reads name/message/stack off what it gets — casting logged
            // "UnknownError / Unknown error occurred" and dropped the reason.
            defaultError instanceof Error
              ? defaultError
              : new Error(String(defaultError)),
          );
          addAlert({
            type: eAlertType.WARNING,
            message: t('messages.paymentMethodNotSetAsDefault'),
          });
        }
      }
    }

    // RET-1974 (R7): with the checkout open, the card the re-read below
    // selects stays selected through later refetches, even when it could not
    // be made the default. Written before the re-read, so a refetch that
    // starts meanwhile already respects it. Not for a card that must follow
    // the default: one saved with `skipDefault` or without becoming it.
    if (
      showCheckoutView &&
      addedPaymentMethodId &&
      !skipDefault &&
      !savedWithoutDefault
    ) {
      preferredPaymentMethodRef.current = {
        workspaceId,
        paymentMethodId: addedPaymentMethodId,
      };
    }

    // D3 (RET-1923): a single fixed 2000ms wait then one re-read used to be
    // the whole strategy — if Stripe took longer than that to attach the
    // card to the customer, this re-read ran before it existed and the
    // added card never showed up selected, with nothing retrying. Re-fetch
    // (without touching component state yet) until the added card appears
    // in the response or the retry budget is spent, keeping the same
    // 2000ms spacing between attempts as before; only then apply the
    // result to state, in the one place (`retrievePaymentMethods`) that
    // already knows how.
    try {
      const paymentMethodsAfterAdd = await retryUntil({
        operation: () =>
          SubscriptionManagerService.getPaymentMethods(workspaceId),
        isDone: (result) =>
          !addedPaymentMethodId ||
          result.paymentMethods.some(
            (method) => method.paymentMethodId === addedPaymentMethodId,
          ),
        intervalMs: 2000,
        maxAttempts: 5,
      });
      // RET-2426: an add that skipped the default does not move the
      // selection either. The card is not charged yet and may be declined,
      // and every surface that shows the selected card would show one the
      // backend does not charge (RC-16). Without a preferred card the
      // selection follows the default; «Cambiar tarjeta» re-reads the list
      // once its charge is confirmed and the default asked for.
      await retrievePaymentMethods(
        skipDefault ? undefined : addedPaymentMethodId,
        paymentMethodsAfterAdd,
      );
      if (zoneCardSavedFallback && addedPaymentMethodId) {
        announceCardSavedInPastDueZone({
          paymentMethodId: addedPaymentMethodId,
          paymentMethods: paymentMethodsAfterAdd.paymentMethods,
          fallback: zoneCardSavedFallback,
        });
      }
    } catch (retrieveError) {
      if (zoneCardSavedFallback) {
        addAlert({
          type: zoneCardSavedFallback.type,
          title: t(zoneCardSavedFallback.titleKey),
          message: t(zoneCardSavedFallback.messageKey),
        });
      }
      // Mirrors `retrievePaymentMethods`' own tolerance: the card was
      // already attached and (when applicable) promoted to default above,
      // so a failure re-reading the list must not undo that or block the
      // success below — it only means the on-screen list may still show
      // stale data until the next load.
      addAlert({
        type: eAlertType.ERROR,
        message: `${retrieveError}`,
      });
    }
    return 'saved';
  }

  // RET-1974: the end of a card add that went through the bank's page. The
  // browser left at `confirmSetup`, so nothing after it ran: the card was
  // neither promoted nor selected. When the URL comes back with the intent,
  // read it from Stripe and end the add as the inline path does. The
  // parameters leave the URL as soon as they are read, so a refresh or a
  // remount never runs it again (KTD6). Each branch waits only for what it
  // needs, up to a ceiling (KTD8).
  useEffect(() => {
    const returned = readSetupIntentReturn(searchParams);
    if (
      returned &&
      redirectReturnRef.current?.setupIntentId !== returned.setupIntentId
    ) {
      const captured: tRedirectReturnProcess = {
        ...returned,
        phase: 'captured',
        ceilingReached: false,
        ceilingTimer: setTimeout(() => {
          captured.ceilingReached = true;
          setRedirectReturnCeilingTick((tick) => tick + 1);
        }, REDIRECT_RETURN_GATE_TIMEOUT_MS),
      };
      redirectReturnRef.current = captured;
      redirectReturnPendingRef.current = true;
      setSearchParams((current) => withoutSetupIntentReturn(current), {
        replace: true,
      });
    }

    const pending = redirectReturnRef.current;
    if (pending?.phase !== 'captured') return;

    // The summary decides between the past-due and the normal notice, and a
    // trial; a refusal from the bank needs neither it nor Stripe.js. Every
    // branch ends in a notice, so all of them wait for its namespace: the page
    // is fresh and `useSuspense` is off, so `t` would answer the raw key.
    const workspaceId = currentWorkspace?.id;
    const ready =
      translationsReady &&
      workspaceId !== undefined &&
      (pending.redirectStatus === 'failed' ||
        (stripe !== null && hasSubscriptionData));
    if (!ready && !pending.ceilingReached) return;

    pending.phase = 'processing';
    void handleRedirectReturn({
      pending,
      workspaceId,
      stripeInstance: stripe,
    });
    // Runs on what the gates wait for; the handler reads the render that
    // opened them.
  }, [
    searchParams,
    currentWorkspace?.id,
    stripe,
    hasSubscriptionData,
    translationsReady,
    redirectReturnCeilingTick,
  ]);

  /**
   * RET-2452 (`tarjeta-guardada`): the card just saved, named in the past-due
   * zone's own words, and selected there. Without the card in the list or an
   * amount to name, the usual notice instead.
   */
  function announceCardSavedInPastDueZone({
    paymentMethodId,
    paymentMethods: listed,
    fallback,
  }: {
    paymentMethodId: string;
    paymentMethods: tPaymentMethod[];
    fallback: { type: eAlertType; titleKey: string; messageKey: string };
  }) {
    const card = listed.find((pm) => pm.paymentMethodId === paymentMethodId);
    const amount = formatAmountOwed({
      amountMinorUnits: subscription.amountRemaining,
      currency: subscription.currency,
    });
    if (card) selectPastDueCard(paymentMethodId);
    if (!card || amount === null) {
      addAlert({
        type: fallback.type,
        title: t(fallback.titleKey),
        message: t(fallback.messageKey),
      });
      return;
    }
    addAlert({
      type: eAlertType.SUCCESS,
      title: t(PAYMENT_METHOD_ADDED_TITLE_KEY),
      message: t('pastDuePayment.cardSaved', {
        brand: brandName(card.brand),
        last4: card.lastDigits,
        amount,
      }),
    });
  }

  /**
   * RET-2452: with the past-due zone on screen, the bank-return notices use
   * the zone's copy (`intent-perdido`, `banco-no-autorizo`).
   */
  const PAST_DUE_REDIRECT_RETURN_KEYS: Record<string, string> = {
    [REDIRECT_RETURN_NOT_CONFIRMED_KEY]: 'pastDuePayment.intentLost',
    [REDIRECT_RETURN_DECLINED_KEY]: 'pastDuePayment.bankNotAuthorized',
  };

  /** RET-1974: a return that ends without a card, said on screen and tracked. */
  function announceRedirectReturnFailure({
    type,
    messageKey,
    declineCode,
  }: {
    type: eAlertType;
    messageKey: string;
    declineCode: string;
  }) {
    const zoneKey = isPastDueZoneActive
      ? PAST_DUE_REDIRECT_RETURN_KEYS[messageKey]
      : undefined;
    addAlert({ type, message: t(zoneKey ?? messageKey) });
    trackSubscriptionEvent(SUBSCRIPTION_EVENTS.PAYMENT_METHOD_ADD, {
      display_name: 'Agregar metodo de pago',
      description: 'Intento fallido de agregar metodo de pago',
      payment_status: 'failed',
      decline_code: declineCode,
    });
  }

  async function handleRedirectReturn({
    pending,
    workspaceId,
    stripeInstance,
  }: {
    pending: tRedirectReturnProcess;
    workspaceId: string | undefined;
    stripeInstance: Stripe | null;
  }) {
    try {
      if (workspaceId === undefined) {
        announceRedirectReturnFailure({
          type: eAlertType.WARNING,
          messageKey: REDIRECT_RETURN_NOT_CONFIRMED_KEY,
          declineCode: 'setup_intent_no_workspace',
        });
        return;
      }
      const dropSnapshot = () =>
        clearRedirectReturnSnapshot(workspaceId, pending.setupIntentId);

      if (pending.redirectStatus === 'failed') {
        dropSnapshot();
        announceRedirectReturnFailure({
          type: eAlertType.ERROR,
          messageKey: REDIRECT_RETURN_DECLINED_KEY,
          declineCode: 'setup_intent_redirect_failed',
        });
        return;
      }

      // Billing's calls below stop at axios' 30 s; Stripe.js has no timeout,
      // so reading the intent back gets the same ceiling as the gates.
      const setupIntent = stripeInstance
        ? await retrieveSetupIntentWithin({
            stripe: stripeInstance,
            clientSecret: pending.clientSecret,
            timeoutMs: REDIRECT_RETURN_GATE_TIMEOUT_MS,
          })
        : undefined;
      if (!setupIntent) {
        dropSnapshot();
        announceRedirectReturnFailure({
          type: eAlertType.WARNING,
          messageKey: REDIRECT_RETURN_NOT_CONFIRMED_KEY,
          declineCode: 'setup_intent_unretrievable',
        });
        return;
      }
      if (setupIntent.status === 'requires_payment_method') {
        dropSnapshot();
        announceRedirectReturnFailure({
          type: eAlertType.ERROR,
          messageKey: REDIRECT_RETURN_DECLINED_KEY,
          declineCode: 'setup_intent_requires_payment_method',
        });
        return;
      }
      // Kept only for a card that may become the default: the debt payment
      // sends it back if the bank then refuses the charge (`M-21`).
      if (setupIntent.status !== 'succeeded' || pending.skipDefault) {
        dropSnapshot();
      }

      const outcome = await finishConfirmedCardAdd({
        workspaceId,
        setupIntent,
        skipDefault: pending.skipDefault,
        // No form and no caller wait for this add: both lived in the page the
        // bank replaced.
        onIntentSucceeded: () => undefined,
        onAddFormDone: () => undefined,
      });
      if (outcome === 'saved' && !pending.skipDefault) {
        trackSubscriptionEvent(SUBSCRIPTION_EVENTS.PAYMENT_METHOD_ADD, {
          display_name: 'Agregar metodo de pago',
          description: 'Agrega un metodo de pago exitoso',
          payment_status: 'success',
        });
      }
    } catch (err) {
      const error = err as BillingError;

      addAlert({
        type: eAlertType.ERROR,
        message: t(`errors.${error?.message ?? ''}`),
      });
      trackSubscriptionEvent(SUBSCRIPTION_EVENTS.PAYMENT_METHOD_ADD, {
        display_name: 'Agregar metodo de pago',
        description: 'Intento fallido de agregar metodo de pago',
        payment_status: 'failed',
      });
    } finally {
      clearTimeout(pending.ceilingTimer);
      pending.phase = 'done';
      redirectReturnPendingRef.current = false;
    }
  }

  async function onCreateSubscription() {
    if (
      isEmpty(currentWorkspace?.id) ||
      isUndefined(currentWorkspace?.id) ||
      processingPayment ||
      stripe === null
    ) {
      return;
    }

    toggleProcessingPayment(true);

    // Capture the plan analytics dimensions before any await so they reflect
    // the intent at submit time even if state mutates during the flow.
    const payPlanType = mapPlanCodeToPlanType(planToBuy?.planCode ?? '');
    const payPlanSubscription = isTrial ? 'trial' : 'plan';

    const extraProducts: tProductToPurchase[] = selectedAddonProducts.map(
      (product) => ({
        product_id: product.uuid,
        quantity: 1,
      }),
    );

    if (
      !isWalletEnabled &&
      monthlyCredits != null &&
      isValidUUID(monthlyCredits.value)
    ) {
      extraProducts.push({
        product_id: monthlyCredits?.value ?? '',
        quantity: 1,
      });
    }

    try {
      LLog.info(
        {
          component: LOG_COMPONENTS.UI,
          method: 'SubscriptionManager.hook',
          operation: LOG_OPERATIONS.ERROR,
        },
        'creating new subscription',
      );
      const newSubscription = await SubscriptionService.createSubscription({
        productId: planToBuy?.productUuid ?? '',
        extraSeats,
        paymentId: selectedPaymentMethod.paymentMethodId,
        wid: currentWorkspace?.id ?? '',
        isTrial,
        couponCode: couponCode.trim() || undefined,
        extraProducts,
      });

      LLog.warn(
        {
          component: LOG_COMPONENTS.UI,
          method: 'SubscriptionManager.hook',
          operation: LOG_OPERATIONS.ERROR,
        },
        'new subscription endpoint called',
        newSubscription,
      );

      if (
        newSubscription.client_secret &&
        newSubscription.requires_3d_secure === true
      ) {
        LLog.warn(
          {
            component: LOG_COMPONENTS.UI,
            method: 'SubscriptionManager.hook',
            operation: LOG_OPERATIONS.ERROR,
          },
          'starting the 3d secure process',
          newSubscription,
        );

        // Store subscription data before redirect for processing in return URL
        const subscriptionData = {
          productId: planToBuy?.productUuid ?? '',
          extraSeats,
          paymentId: selectedPaymentMethod.paymentMethodId,
          isTrial,
          extraProducts,
        };

        localStorage.setItem(
          'pending_subscription',
          JSON.stringify(subscriptionData),
        );

        const result = await stripe.confirmCardPayment(
          newSubscription.client_secret,
        );

        const { id, status } = extractPaymentIntentData(result);

        LLog.warn(
          {
            component: LOG_COMPONENTS.UI,
            method: 'SubscriptionManager.hook',
            operation: LOG_OPERATIONS.ERROR,
          },
          'info obtained from paymentIntent',
          { id: id, status: status },
        );

        const confirm3DSecurePaymentResponse =
          await SubscriptionManagerService.confirm3DSecurePayment(
            currentWorkspace.id,
            id,
            status,
          );

        if (
          !confirm3DSecurePaymentResponse.success ||
          confirm3DSecurePaymentResponse.data.subscription_deleted
        ) {
          console.error(
            'Confirm3DSecurePaymentResponse:',
            confirm3DSecurePaymentResponse,
          );
          throw new Error(t('messages.errorConfirming3DSecurePayment'));
        }
      }

      // Calculate total checkout price for GTM event
      const extraSeatsPrice = extraSeats * (planToBuy?.extraSeatPrice ?? 0);
      const selectedAddonProductsPrice = selectedAddonProducts.reduce(
        (total, product) => total + (product.price ?? 0),
        0,
      );
      const monthlyCreditsPrice = isWalletEnabled
        ? 0
        : (monthlyCredits?.price ?? 0);
      const totalCheckoutPrice =
        (planToBuy?.totalPrice ?? 0) +
        extraSeatsPrice +
        selectedAddonProductsPrice +
        monthlyCreditsPrice;

      // Event #2: Track Purchase event when free user completes first purchase
      // This should only fire for new subscriptions (not upgrades)
      if (
        !workspaceInfo?.isActive &&
        currentUser &&
        planToBuy &&
        currentWorkspace?.id
      ) {
        const purchaseAmount = Math.round(totalCheckoutPrice).toString();

        trackPurchaseEvent({
          event: 'purchase',
          email: currentUser.email ?? '',
          name: currentUser.name ?? '',
          phone: currentUser.phoneNumber?.toString() ?? '',
          date: new Date().toISOString(),
          amount: purchaseAmount,
          currency: planToBuy.currency.toLowerCase(),
          recurrencyType: planToBuy.frequencyName,
          plan: getPlanName(planToBuy.planCode),
          workspaceId: currentWorkspace.id,
          isTrial: isTrial,
          stripeCustomerId:
            newSubscription.customerId?.toString() ??
            subscription.customerId ??
            '',
        });
      }

      getSummary();
      togglePaymentCompletedModal(true);
      trackEvent('front.settings.purchase_succeed', {
        cta_type: ctaType ?? '',
      });
      trackSubscriptionEvent(SUBSCRIPTION_EVENTS.PAY_PLAN, {
        display_name: 'Pagar Plan',
        description: 'Pago una suscripcion',
        payment_status: 'success',
        plan_type: payPlanType,
        subscription: payPlanSubscription,
      });

      activateWorkspace();

      // Reset checkout states and return to overview
      returnToOverview();
    } catch (err) {
      const error = err as BillingError | undefined;
      // This block used to run its own `errors.${message}` lookup whose fallback
      // was the RAW message — which is how a customer was shown the literal
      // string "incorrect_cvc_incorrect_cvc" instead of the Spanish CVC copy
      // that already existed in the locale. Route it through the shared resolver
      // (defined above, in this same file) like every other payment surface.
      const declineMessage = resolvePurchaseErrorMessage(error);
      addAlert({
        type: eAlertType.ERROR,
        message: declineMessage,
      });
      trackSubscriptionEvent(SUBSCRIPTION_EVENTS.PAY_PLAN, {
        display_name: 'Pagar Plan',
        description: 'Pago una suscripcion',
        payment_status: 'failed',
        decline_code: error?.message ?? '',
        decline_copy_resolved: isSpecificDeclineCopy(declineMessage),
        plan_type: payPlanType,
        subscription: payPlanSubscription,
      });
      if (appliedCouponDiscount !== null) {
        setCouponError(t('checkoutSummary.coupon.notApplied'));
      }
    }
    toggleProcessingPayment(false);
  }

  async function checkAndHandleWAPIDowngrade(): Promise<boolean> {
    const currentPlanHasWAPI =
      subscription.planCode === ePlanCode.PLAN_PROFESSIONAL ||
      subscription.planCode === ePlanCode.PLAN_ENTERPRISE;

    const newPlanHasWAPI =
      planToBuy?.planCode === ePlanCode.PLAN_PROFESSIONAL ||
      planToBuy?.planCode === ePlanCode.PLAN_ENTERPRISE;

    const isWAPIDowngrade = currentPlanHasWAPI && !newPlanHasWAPI;

    if (!isWAPIDowngrade) {
      return false;
    }

    try {
      const data = await IntegrationsService.getIntegrations({
        uuid: currentWorkspace?.id as string,
      });

      let pageId = '';

      data.data.integrations.forEach((integration) => {
        if (
          integration.label === eIntegrationType.MESSENGER &&
          integration.state === eIntegrationStatus.CONNECTED
        ) {
          const selectedPage = integration?.pages?.find(
            (page) => page.isSelected,
          );
          if (selectedPage) {
            pageId = selectedPage.id;
          }
        }
      });

      if (pageId) {
        await DashboardService.disconnectAndUnsubscribe(pageId);

        LLog.info(
          {
            component: LOG_COMPONENTS.SUBSCRIPTION,
            method: 'SubscriptionManager.hook',
            operation: LOG_OPERATIONS.UI_INTERACTION,
          },
          'WhatsApp Business disconnected due to plan downgrade',
          {
            from_plan: subscription.planCode,
            to_plan: planToBuy?.planCode,
            page_id: pageId,
          },
        );

        return true;
      }

      return false;
    } catch (error) {
      LLog.error(
        {
          component: LOG_COMPONENTS.SUBSCRIPTION,
          method: 'SubscriptionManager.hook',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error disconnecting WhatsApp Business during downgrade',
        error as Error,
      );

      return false;
    }
  }

  async function onUpgradeSubscription() {
    if (isEmpty(currentWorkspace?.id) || processingPayment || !stripe) {
      return;
    }

    toggleProcessingPayment(true);

    // Capture the plan analytics dimensions before any await so they reflect
    // the intent at submit time even if state mutates during the flow.
    const payPlanType = mapPlanCodeToPlanType(planToBuy?.planCode ?? '');
    const payPlanSubscription = isTrial ? 'trial' : 'plan';
    // RET-2364: set once the payment carries the cards noted at checkout open;
    // the `catch` below reads the cards again only for such a payment.
    let sentPreviousPaymentMethods = false;

    try {
      const isDowngradingFromWAPI = await checkAndHandleWAPIDowngrade();

      const extraProducts: tProductToPurchase[] = selectedAddonProducts.map(
        (product) => ({
          product_id: product.uuid,
          quantity: 1,
        }),
      );

      // Add monthly credits to extraProducts for Professional/Advanced plans
      if (
        !isWalletEnabled &&
        (planToBuy?.planCode === ePlanCode.PLAN_PROFESSIONAL ||
          planToBuy?.planCode === ePlanCode.PLAN_ENTERPRISE) &&
        isValidUUID(monthlyCredits?.value ?? '')
      ) {
        extraProducts.push({
          product_id: monthlyCredits?.value ?? '',
          quantity: 1,
        });
      }

      // RET-1120: coupons are NOT applied on the upgrade path from the front.
      // Customers apply coupons only at new-subscription signup (which routes to
      // SubscriptionService.createSubscription above and passes couponCode inline
      // to Stripe.subscriptions.create — Stripe discounts the first invoice
      // correctly). Discounts on existing subscriptions are an agent-only
      // operation via the backoffice ApplyCouponToWorkspace Nova action. The
      // backend (leadbilling UpgradeSubscriptionRequest) 422s coupon_code on
      // this path; we don't send it from the front.
      //
      // RET-2364: `previous_payment_methods` is the pre-3DS request only. A
      // card that asks for 3DS is not a bank refusal, so the call after
      // authentication (upgradeRequest3D) does not carry it.
      const previousPaymentMethods = previousPaymentMethodsRef.current;
      sentPreviousPaymentMethods =
        previousPaymentMethods.plan !== null ||
        previousPaymentMethods.account !== null;
      const upgradeRequest: tSubscriptionUpgradeRequest = {
        new_plan_name: planToBuy?.name ?? subscription.name ?? '',
        frequency: planToBuy?.frequencyName ?? subscription.frequency,
        seats: (planToBuy?.includedSeats ?? 0) + extraSeats,
        payment_method_id: selectedPaymentMethod.paymentMethodId,
        ...(extraProducts.length > 0 && { extra_products: extraProducts }),
        stripe_confirm: true,
        ...(sentPreviousPaymentMethods && {
          previous_payment_methods: previousPaymentMethods,
        }),
      };

      const response = await SubscriptionManagerService.upgradeSubscription(
        currentWorkspace?.id ?? '',
        upgradeRequest,
      );

      // Check if 3D Secure authentication is required
      if (
        response.data?.requires_action &&
        response.data?.payment_intent_client_secret
      ) {
        LLog.info(
          {
            component: LOG_COMPONENTS.UI,
            method: 'SubscriptionManager.hook',
            operation: LOG_OPERATIONS.UI_INTERACTION,
          },
          'Starting 3D Secure authentication for subscription upgrade',
        );

        // Trigger 3D Secure authentication
        const result = await stripe.confirmPayment({
          clientSecret: response.data.payment_intent_client_secret,
          redirect: 'if_required' as const,
        });

        if (result?.error) {
          // Authentication failed or was cancelled
          LLog.error(
            {
              component: LOG_COMPONENTS.UI,
              method: 'SubscriptionManager.hook',
              operation: LOG_OPERATIONS.ERROR,
            },
            'Error during 3D Secure authentication for subscription upgrade',
            result.error as unknown as Error,
          );

          // A 3DS failure never reaches leadbilling, so `result.error.message`
          // is Stripe.js's ENGLISH sentence. Rebuild the composite from its
          // code/decline_code and resolve the same localized copy the non-3DS
          // paths use, instead of showing English in a Spanish UI.
          const declineMessage3ds = resolveBillingErrorMessage(
            stripeJsErrorToBillingPayload(result.error),
            t,
          );
          addAlert({
            type: eAlertType.ERROR,
            message: declineMessage3ds,
          });

          trackSubscriptionEvent(SUBSCRIPTION_EVENTS.PAY_PLAN, {
            display_name: 'Pagar Plan',
            description: 'Pago una suscripcion',
            payment_status: 'failed',
            decline_code: result.error.decline_code ?? result.error.code ?? '',
            decline_copy_resolved: isSpecificDeclineCopy(declineMessage3ds),
            plan_type: payPlanType,
            subscription: payPlanSubscription,
          });
          toggleProcessingPayment(false);
          return;
        }

        LLog.info(
          {
            component: LOG_COMPONENTS.UI,
            method: 'SubscriptionManager.hook',
            operation: LOG_OPERATIONS.UI_INTERACTION,
          },
          '3D Secure authentication completed successfully for subscription upgrade',
        );

        // After successful 3D Secure authentication, confirm the payment with the backend
        if (result?.paymentIntent && currentWorkspace?.id) {
          // RET-1120: same as the pre-3DS upgrade payload above — coupon_code is
          // NOT sent on the upgrade path. See comment on the first upgradeRequest.
          const upgradeRequest3D: tSubscriptionUpgradeRequest = {
            new_plan_name: planToBuy?.name ?? subscription.name ?? '',
            frequency: planToBuy?.frequencyName ?? subscription.frequency,
            seats: (planToBuy?.includedSeats ?? 0) + extraSeats,
            payment_method_id: selectedPaymentMethod.paymentMethodId,
            ...(extraProducts.length > 0 && { extra_products: extraProducts }),
            stripe_confirm: false,
          };

          const response = await SubscriptionManagerService.upgradeSubscription(
            currentWorkspace?.id ?? '',
            upgradeRequest3D,
          );

          if (!response.success) {
            LLog.error(
              {
                component: LOG_COMPONENTS.UI,
                method: 'SubscriptionManager.hook',
                operation: LOG_OPERATIONS.ERROR,
              },
              'Error confirming 3D Secure payment for subscription upgrade',
            );

            addAlert({
              type: eAlertType.ERROR,
              message: t('messages.errorConfirming3DSecurePayment'),
            });

            trackSubscriptionEvent(SUBSCRIPTION_EVENTS.PAY_PLAN, {
              display_name: 'Pagar Plan',
              description: 'Pago una suscripcion',
              payment_status: 'failed',
              plan_type: payPlanType,
              subscription: payPlanSubscription,
            });
            toggleProcessingPayment(false);
            return;
          }
        }
      }

      // Wait for the subscription to be updated
      await new Promise<void>((resolve) =>
        setTimeout(async () => {
          await getSummary();
          resolve();
        }, 2000),
      );

      if (isDowngradingFromWAPI) {
        addAlert({
          type: eAlertType.INFO,
          message: t('messages.whatsappBusinessDisconnectedDueToDowngrade'),
        });
      }

      togglePaymentCompletedModal(true);
      trackEvent('front.settings.purchase_succeed', {
        cta_type: ctaType ?? '',
        wapi_downgrade: isDowngradingFromWAPI,
      });
      trackSubscriptionEvent(SUBSCRIPTION_EVENTS.PAY_PLAN, {
        display_name: 'Pagar Plan',
        description: 'Pago una suscripcion',
        payment_status: 'success',
        plan_type: payPlanType,
        subscription: payPlanSubscription,
      });

      // Reset checkout states and return to overview
      returnToOverview();
    } catch (err) {
      // RET-1475: a card-declined 402 now reaches here (bff-web forwards it
      // instead of masking it as 424). Route through the shared resolver so the
      // user sees actionable, localized decline copy — never the raw
      // "Payment failed: card_declined_..." string the upgrade endpoint sends.
      const error = err as (BillingError & { error_code?: string }) | undefined;
      addAlert({
        type: eAlertType.ERROR,
        message: error
          ? resolvePurchaseErrorMessage(error)
          : t('messages.errorProcessingPayment'),
      });
      trackSubscriptionEvent(SUBSCRIPTION_EVENTS.PAY_PLAN, {
        display_name: 'Pagar Plan',
        description: 'Pago una suscripcion',
        payment_status: 'failed',
        plan_type: payPlanType,
        subscription: payPlanSubscription,
      });
      if (appliedCouponDiscount !== null) {
        setCouponError(t('checkoutSummary.coupon.notApplied'));
      }
      if (sentPreviousPaymentMethods) {
        await rereadPaymentMethodsAfterFailedPlanChange();
      }
    }
    toggleProcessingPayment(false);
  }

  /**
   * RET-2364: a plan change that failed after the checkout promoted a card.
   * Billing puts the previous cards back when the bank refused the charge, but
   * this screen still holds the refused card as default and selected: a retry
   * would show it, and leaving and reopening the checkout would note it as the
   * "previous" card. Read the cards again and follow the plan card of that
   * read — the card that really charges the plan now, restored or not.
   *
   * Silent for the customer: the failure already has its notice, and
   * `retrievePaymentMethods` alone would add a second one with `${err}` if the
   * read failed, so the read is done here first. Nothing is thrown.
   */
  async function rereadPaymentMethodsAfterFailedPlanChange() {
    if (!currentWorkspace?.id) return;
    try {
      const fresh = await SubscriptionManagerService.getPaymentMethods(
        currentWorkspace.id,
      );
      // RET-1974: billing put the cards back; the selection follows them.
      preferredPaymentMethodRef.current = undefined;
      await retrievePaymentMethods(fresh.planPaymentMethodId, fresh);
    } catch (rereadError) {
      LLog.warn(
        {
          component: LOG_COMPONENTS.SUBSCRIPTION,
          method: 'SubscriptionManager.hook',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Could not read the payment methods again after a failed plan change',
        { error: String(rereadError) },
      );
    }
  }

  async function applyCoupon() {
    if (!couponCode.trim()) return;
    // RET-1120 + RET-1157 entry guard — coupon application is restricted to
    // new-subscription signup. On upgrade the backend 422s any coupon_code,
    // so the validateCoupon network call would mislead the user with a
    // "valid coupon" success that never reaches the payload. RET-1157
    // re-exposed the UI behind a `!isUpgradeContext` gate in CheckoutSummary;
    // this entry guard is LOAD-BEARING for the narrow race window between
    // a click on Apply and the next render after `workspaceInfo.isActive`
    // flips to true (paired with the mid-await ref re-check below).
    if (isUpgradeContext) {
      return;
    }
    // RET-1157 / JFR-004: abort any prior in-flight validation before starting
    // a new one. Prevents an older resolution from racing the new one to set
    // coupon state (e.g., the user types fast and clicks Apply twice).
    couponAbortRef.current?.abort();
    const controller = new AbortController();
    couponAbortRef.current = controller;
    setCouponError(null);
    setApplyingCoupon(true);
    try {
      const result = await SubscriptionService.validateCoupon(
        currentWorkspace?.id ?? '',
        couponCode.trim(),
        controller.signal,
      );
      // RET-1157 / JFR-001: context may have flipped to upgrade during the
      // await (parallel checkout completion, WebSocket-triggered
      // activateWorkspace). Read the LIVE value via the ref kept in sync by
      // the useEffect above — the closure-captured `isUpgradeContext` is
      // stale by now. Skip the coupon state writes that would land on a
      // hidden UI subtree.
      if (controller.signal.aborted || isUpgradeContextRef.current) {
        return;
      }
      if (result.valid && result.discount_value !== undefined && result.discount_type) {
        // fixed_amount values arrive in currency-native units (e.g. 100 = "100 MXN off") —
        // normalize to cents so downstream math (base - discount) and formatPrice agree.
        const normalizedDiscount = normalizeCouponDiscountValue(result.discount_value, result.discount_type);
        setAppliedCouponDiscount(normalizedDiscount);
        if (result.discount_type === 'percentage' || result.discount_type === 'fixed_amount') {
          setCouponDiscountType(result.discount_type);
        }
        if (result.duration === 'once' || result.duration === 'forever' || result.duration === 'repeating') {
          setCouponDuration(result.duration);
          setCouponDurationInMonths(result.duration === 'repeating' ? (result.duration_in_months ?? null) : null);
        } else {
          setCouponDuration(null);
          setCouponDurationInMonths(null);
        }
      } else {
        setAppliedCouponDiscount(null);
        setCouponDiscountType(null);
        setCouponDuration(null);
        setCouponDurationInMonths(null);
      }
    } catch (e: unknown) {
      // RET-1157: a deliberate abort (re-apply, removeCoupon, unmount, or
      // mid-flight upgrade flip) must not surface as an error toast.
      if (controller.signal.aborted || isUpgradeContextRef.current) {
        return;
      }
      const axiosErr = e as AxiosError<{ error_code?: string }>;
      const errorCode =
        axiosErr.response?.data?.error_code ??
        'coupon_unknown_error';
      setCouponError(t(`couponErrors.${errorCode}`));
      setAppliedCouponDiscount(null);
      setCouponDiscountType(null);
      setCouponDuration(null);
      setCouponDurationInMonths(null);
    } finally {
      if (couponAbortRef.current === controller) {
        couponAbortRef.current = null;
      }
      setApplyingCoupon(false);
    }
  }

  // RET-1120 (/ce:review M-001 / F1): single source of truth for "is this an
  // upgrade of an existing sub vs a new-subscription purchase". Used by
  // handlePayment (routing) and applyCoupon (context guard). Guarded against
  // the `planToBuy === undefined` false-positive that would otherwise fire while
  // the user is still on the plan-selection step.
  // Gate on an active *subscription* (live & modifiable in Stripe), not on
  // workspaceInfo.isActive (a workspace-enabled flag). A churned customer
  // re-subscribing has an active workspace but no active subscription; routing
  // them through the change-plan/upgrade flow hits a canceled Stripe sub and
  // fails. With no upgradeable subscription, handlePayment falls to the create
  // flow instead.
  const isUpgradeContext = useMemo(
    () =>
      shouldUseUpgradeContext({
        subscriptionStatus: subscription.status,
        subscriptionPlanCode: subscription.planCode,
        subscriptionFrequency: subscription.frequency,
        hasPlanToBuy: Boolean(planToBuy),
        planToBuyPlanCode: planToBuy?.planCode,
        planToBuyFrequency: planToBuy?.frequencyName,
      }),
    [
      subscription.status,
      planToBuy,
      subscription.planCode,
      subscription.frequency,
    ],
  );

  // RET-1157 / JFR-001: keep the ref in sync with the memo so in-flight
  // applyCoupon continuations can read the LIVE value after their await
  // resolves (closure-captured `isUpgradeContext` is stale by then). Also
  // abort any in-flight coupon validation when the context flips to upgrade
  // mid-flight so the network resolution does not race the unmount.
  useEffect(() => {
    isUpgradeContextRef.current = isUpgradeContext;
    if (isUpgradeContext && couponAbortRef.current) {
      couponAbortRef.current.abort();
      couponAbortRef.current = null;
    }
  }, [isUpgradeContext]);

  // RET-1157 / JFR-004: abort any pending coupon validation on hook unmount
  // so resolutions cannot land on a destroyed tree.
  useEffect(
    () => () => {
      couponAbortRef.current?.abort();
      couponAbortRef.current = null;
    },
    [],
  );

  // Determine which function to use based on upgrade scenario
  const handlePayment = () => {
    if (isUpgradeContext) {
      onUpgradeSubscription();
    } else {
      onCreateSubscription();
    }
  };

  async function updateWatermarkPlan(watermarkPlanRemoval: boolean) {
    const watermarkPlanRemovalBeingUpdated =
      watermarkPlanRemoval !== watermarkRemoval;

    if (updatingPlan || currentWorkspace?.id === undefined) {
      return;
    }
    toggleUpdatingPlan(true);
    try {
      if (watermarkPlanRemovalBeingUpdated) {
        let confirmPayment: boolean = false;
        const watermarkPlanRemovalProduct = products?.allRelatedProducts?.find(
          (product) => product.productType === eProductType.LEADBOT_PREMIUM,
        );

        const purchaseIntentId = ensurePurchaseIntentId();
        if (watermarkPlanRemovalProduct) {
          if (watermarkPlanRemoval) {
            const response = await SubscriptionManagerService.purchaseProduct(
              currentWorkspace.id,
              {
                products: [
                  {
                    product_id: watermarkPlanRemovalProduct.uuid,
                    quantity: 1,
                  },
                ],
                purchaseIntentId,
              },
            );

            if (
              response.data.status === 'requires_action' &&
              response.data.payment_intent_client_secret
            ) {
              confirmPayment = true;
              const result = await stripe?.confirmPayment({
                clientSecret: response.data.payment_intent_client_secret,
                redirect: 'if_required' as const,
              });

              if (result?.error) {
                // This point is only reached if there's an immediate error when
                // creating the PaymentMethod. Show the error to your customer (for example, payment details incomplete)
                LLog.error(
                  {
                    component: LOG_COMPONENTS.UI,
                    method: 'SubscriptionManager.hook',
                    operation: LOG_OPERATIONS.ERROR,
                  },
                  'Error in SubscriptionManager.hook',
                  result.error as unknown as Error,
                );

                return;
              }
            }
          } else if (watermarkRemovalId) {
            await SubscriptionManagerService.cancelProduct(
              currentWorkspace.id,
              [
                {
                  product_id: watermarkPlanRemovalProduct.uuid,
                  quantity: 1,
                  workspace_override_id: watermarkRemovalId,
                },
              ],
            );
          }
        } else if (isLegacyPlan && watermarkRemoval) {
          await SubscriptionManagerService.cancelProduct(currentWorkspace.id, [
            {
              product_id: 'chatbot_legacy',
              quantity: 1,
              workspace_override_id: 0,
            },
          ]);
        }

        if (confirmPayment && watermarkPlanRemovalProduct) {
          await SubscriptionManagerService.purchaseProduct(
            currentWorkspace.id,
            {
              products: [
                {
                  product_id: watermarkPlanRemovalProduct.uuid,
                  quantity: 1,
                },
              ],
              confirmStripe: false,
              purchaseIntentId,
            },
          );
        }
      }

      await getSummary();

      addAlert({
        type: eAlertType.SUCCESS,
        message: t('messages.planUpdatedSuccessfully'),
      });
    } catch (err) {
      const error = err as BillingError;
      addAlert({
        type: eAlertType.ERROR,
        message: resolvePurchaseErrorMessage(error),
      });
    }
    toggleUpdatingPlan(false);
    returnFromCheckout();
  }

  async function updateAdvancedLeadbotPlan(advancedLeadbotEnabled: boolean) {
    const advancedLeadbotBeingUpdated =
      advancedLeadbotEnabled !== leadbotLevelsAccess;

    if (updatingPlan || currentWorkspace?.id === undefined) {
      return;
    }
    toggleUpdatingPlan(true);
    try {
      if (advancedLeadbotBeingUpdated) {
        // RET-1285: captured before the await for consistency with the
        // 3DS-capable flows (the ref memoization already makes a second call
        // return the same id, but the const keeps all four flows uniform).
        const purchaseIntentId = ensurePurchaseIntentId();
        const advancedLeadbotProduct = products?.allRelatedProducts?.find(
          (product) => product.productType === eProductType.LEADBOT_LEVELS,
        );

        if (advancedLeadbotProduct) {
          if (advancedLeadbotEnabled) {
            await SubscriptionManagerService.purchaseProduct(
              currentWorkspace.id,
              {
                products: [
                  {
                    product_id: advancedLeadbotProduct.uuid,
                    quantity: 1,
                  },
                ],
                purchaseIntentId,
              },
            );
          } else if (leadbotLevelsAccessId) {
            await SubscriptionManagerService.cancelProduct(
              currentWorkspace.id,
              [
                {
                  product_id: advancedLeadbotProduct.uuid,
                  quantity: 1,
                  workspace_override_id: leadbotLevelsAccessId,
                },
              ],
            );
          }
        }
      }

      await getSummary();

      addAlert({
        type: eAlertType.SUCCESS,
        message: t('messages.planUpdatedSuccessfully'),
      });
    } catch (err) {
      const error = err as BillingError;
      addAlert({
        type: eAlertType.ERROR,
        message: resolvePurchaseErrorMessage(error),
      });
    }
    toggleUpdatingPlan(false);
    returnFromCheckout();
  }

  async function updateLeadLimitAddonPlan(leadLimitAddonEnabled: boolean) {
    const leadLimitAddonBeingUpdated =
      leadLimitAddonEnabled !== isLeadLimitAddonActive();

    if (updatingPlan || currentWorkspace?.id === undefined) {
      return;
    }
    toggleUpdatingPlan(true);
    try {
      if (leadLimitAddonBeingUpdated) {
        let confirmPayment: boolean = false;
        // RET-1285: capture once so the post-3DS re-call reuses the same id.
        const purchaseIntentId = ensurePurchaseIntentId();
        const leadLimitAddonProduct = products?.allRelatedProducts?.find(
          (product) => product.productType === eProductType.LEAD_LIMIT_ADDON,
        );

        if (leadLimitAddonProduct) {
          if (leadLimitAddonEnabled) {
            // Track purchase CTA clicked if coming from modal
            const purchaseSource = searchParams.get('openLeadLimitAddon')
              ? 'limit_warning_modal'
              : 'organic';

            try {
              const response = await SubscriptionManagerService.purchaseProduct(
                currentWorkspace.id,
                {
                  products: [
                    {
                      product_id: leadLimitAddonProduct.uuid,
                      quantity: 1,
                    },
                  ],
                  purchaseIntentId,
                },
              );

              if (
                response.data.status === 'requires_action' &&
                response.data.payment_intent_client_secret
              ) {
                confirmPayment = true;
                const result = await stripe?.confirmPayment({
                  clientSecret: response.data.payment_intent_client_secret,
                  redirect: 'if_required' as const,
                });

                if (result?.error) {
                  // This point is only reached if there's an immediate error when
                  // creating the PaymentMethod. Show the error to your customer (for example, payment details incomplete)
                  LLog.error(
                    {
                      component: LOG_COMPONENTS.UI,
                      method: 'SubscriptionManager.hook',
                      operation: LOG_OPERATIONS.ERROR,
                    },
                    'Error in SubscriptionManager.hook',
                    result.error as unknown as Error,
                  );

                  // Reset loading state and show error before returning
                  toggleUpdatingPlan(false);
                  // Same 3DS reasoning as the other confirmCardPayment sites:
                  // resolve localized copy rather than echo Stripe.js English.
                  addAlert({
                    type: eAlertType.ERROR,
                    message: resolveBillingErrorMessage(
                      stripeJsErrorToBillingPayload(result.error),
                      t,
                    ),
                  });
                  return;
                }
              }

              // Track successful purchase
              trackEvent('front.settings.addon_purchase_completed', {
                addon_type: 'conversations_boost',
                addon_conversations: LEAD_LIMIT_ADDON_INCREMENT,
                purchase_source: purchaseSource,
                plan: getPlanName(subscription.planCode),
              });
            } catch (err) {
              const error = err as unknown as BillingError & { code?: string };
              const errorCode = error.code || '';
              const errorMessage =
                error.message || (err as Error)?.message || 'Unknown error';

              // Determine failure reason
              let failureReason = 'other';
              if (
                errorCode.includes('card') ||
                errorCode.includes('declined')
              ) {
                failureReason = 'payment_declined';
              } else if (
                errorCode.includes('network') ||
                errorMessage.includes('network')
              ) {
                failureReason = 'network_error';
              } else if (
                errorCode.includes('stripe') ||
                errorMessage.includes('stripe')
              ) {
                failureReason = 'stripe_error';
              }

              // Track failed purchase
              trackEvent('front.settings.addon_purchase_failed', {
                addon_type: 'conversations_boost',
                failure_reason: failureReason,
                error_code: errorCode || 'unknown',
                error_message: errorMessage,
                plan: getPlanName(subscription.planCode),
              });

              throw err;
            }
          } else if (leadLimitAddonId) {
            await SubscriptionManagerService.cancelProduct(
              currentWorkspace.id,
              [
                {
                  product_id: leadLimitAddonProduct.uuid,
                  quantity: 1,
                  workspace_override_id: leadLimitAddonId,
                },
              ],
            );

            // Track addon cancellation
            trackEvent('front.settings.addon_cancelled', {
              addon_type: 'conversations_boost',
              plan: getPlanName(subscription.planCode),
            });
          } else {
            throw new Error('Lead limit addon not found');
          }
        }

        if (confirmPayment && leadLimitAddonProduct) {
          await SubscriptionManagerService.purchaseProduct(
            currentWorkspace.id,
            {
              products: [
                {
                  product_id: leadLimitAddonProduct.uuid,
                  quantity: 1,
                },
              ],
              confirmStripe: false,
              purchaseIntentId,
            },
          );
        }
      }

      await getSummary();

      addAlert({
        type: eAlertType.SUCCESS,
        message: t('messages.planUpdatedSuccessfully'),
      });
    } catch (err) {
      const error = err as BillingError;
      addAlert({
        type: eAlertType.ERROR,
        message: resolvePurchaseErrorMessage(error),
      });
    }
    toggleUpdatingPlan(false);
    returnFromCheckout();
  }

  async function updateSeatsPlan(extraSeats: number) {
    const seatsBeingAdded = extraSeats > subscription.purchasedSeats;
    const seatsBeingRemoved = extraSeats < subscription.purchasedSeats;

    if (updatingPlan || currentWorkspace?.id === undefined) {
      return;
    }
    toggleUpdatingPlan(true);
    try {
      // RET-2032: `findCatalogProduct` is the single resolver shared with the
      // modal's price/enabled-button logic and the "Agrega más usuarios"
      // card's visibility gate — checking `availableForPurchase` first, then
      // falling back to `allRelatedProducts`, tolerant to a dirty catalog
      // entry with a missing/null `productType` and case-insensitive (some
      // environments send it lowercase). Before this, the purchase target
      // here only ever looked at `allRelatedProducts`, so a SEATS entry that
      // lived only in `availableForPurchase` could enable the button (via the
      // modal's lookup) and then throw here on click.
      const extraSeatsProduct = findCatalogProduct(products, eProductType.SEATS);

      // RET-2032: the loaded catalog is missing the SEATS product entirely.
      // Without this guard neither branch below runs, no request is made,
      // and execution fell through to `getSummary()` + the SUCCESS alert —
      // telling the customer their purchase went through when nothing was
      // charged. The legacy remove path doesn't need a catalog entry (it
      // targets a hardcoded `seat_legacy` product id), so it's exempt.
      const needsCatalogSeatsProduct =
        (seatsBeingAdded || seatsBeingRemoved) &&
        !(isLegacyPlan && seatsBeingRemoved);
      if (needsCatalogSeatsProduct && !extraSeatsProduct) {
        throw new Error(
          'SEATS product not found in the loaded catalog (RET-2032)',
        );
      }

      if (seatsBeingAdded && extraSeatsProduct) {
        let confirmPayment: boolean = false;
        // RET-1285: capture the intent once so the post-3DS re-call below
        // reuses the exact same id (server-side dedup authority).
        const purchaseIntentId = ensurePurchaseIntentId();
        const response = await SubscriptionManagerService.purchaseProduct(
          currentWorkspace.id,
          {
            products: [
              {
                product_id: extraSeatsProduct.uuid,
                quantity: extraSeats - subscription.purchasedSeats,
              },
            ],
            purchaseIntentId,
          },
        );

        if (
          response.data.status === 'requires_action' &&
          response.data.payment_intent_client_secret
        ) {
          confirmPayment = true;
          const result = await stripe?.confirmPayment({
            clientSecret: response.data.payment_intent_client_secret,
            redirect: 'if_required' as const,
          });

          if (result?.error) {
            // This point is only reached if there's an immediate error when
            // creating the PaymentMethod. Show the error to your customer (for example, payment details incomplete)
            LLog.error(
              {
                component: LOG_COMPONENTS.UI,
                method: 'SubscriptionManager.hook',
                operation: LOG_OPERATIONS.ERROR,
              },
              'Error in SubscriptionManager.hook',
              result.error as unknown as Error,
            );

            // RET-2032: the customer was only told via a log line they never
            // see — the button stayed in "loading" and nothing explained why.
            addAlert({
              type: eAlertType.ERROR,
              message: t('messages.errorConfirming3DSecurePayment'),
            });

            return;
          }
        }

        if (confirmPayment) {
          await SubscriptionManagerService.purchaseProduct(
            currentWorkspace.id,
            {
              products: [
                {
                  product_id: extraSeatsProduct.uuid,
                  quantity: extraSeats - subscription.purchasedSeats,
                },
              ],
              confirmStripe: false,
              purchaseIntentId,
            },
          );
        }

        setCurrentSettingsInfo((prevState) => ({
          ...prevState,
          seats: {
            used: prevState.seats.used,
            available: extraSeats - subscription.purchasedSeats,
          },
        }));
      }

      if (seatsBeingRemoved && extraSeatsProduct) {
        if (
          currentSettingsInfo.seats.used >
          extraSeats + subscription.includedSeats
        ) {
          addAlert({
            type: eAlertType.ERROR,
            message: t('messages.cannotRemoveMoreSeats'),
          });
          return;
        }
        await SubscriptionManagerService.cancelProduct(currentWorkspace.id, [
          {
            product_id: extraSeatsProduct.uuid,
            quantity: subscription.purchasedSeats - extraSeats,
            workspace_override_id: 0,
          },
        ]);
        setCurrentSettingsInfo((prevState) => ({
          ...prevState,
          seats: {
            used: prevState.seats.used,
            available: subscription.purchasedSeats - extraSeats,
          },
        }));
      }

      if (isLegacyPlan && seatsBeingRemoved) {
        if (
          currentSettingsInfo.seats.used >
          extraSeats + subscription.includedSeats
        ) {
          addAlert({
            type: eAlertType.ERROR,
            message: t('messages.cannotRemoveMoreSeats'),
          });
          return;
        }
        await SubscriptionManagerService.cancelProduct(currentWorkspace.id, [
          {
            product_id: 'seat_legacy',
            quantity: subscription.purchasedSeats - extraSeats,
            workspace_override_id: 0,
          },
        ]);
        setCurrentSettingsInfo((prevState) => ({
          ...prevState,
          seats: {
            used: prevState.seats.used,
            available: subscription.purchasedSeats - extraSeats,
          },
        }));
      }

      await getSummary();

      addAlert({
        type: eAlertType.SUCCESS,
        message: t('messages.planUpdatedSuccessfully'),
      });
    } catch (err) {
      const error = err as BillingError;
      addAlert({
        type: eAlertType.ERROR,
        message: resolvePurchaseErrorMessage(error),
      });
    } finally {
      // RET-2032: every early `return` above (3DS confirmation error,
      // cannotRemoveMoreSeats on either the modern or legacy path) sat inside
      // the `try`, before these two calls — so they never ran and the button
      // stayed in "loading" forever. `finally` guarantees them on every exit.
      toggleUpdatingPlan(false);
      returnFromCheckout();
    }
  }

  /**
   * The cancellation form's submit handler. Resolves true only when the
   * cancellation was confirmed as scheduled, so the retention flow can report it
   * from the confirmed outcome. A failure shows an error localized by the
   * refusal code (the relayed `message` is never shown) and never the success
   * toast.
   */
  async function cancelSubscription(values: tCancelForm): Promise<boolean> {
    if (cancellingSubscription) {
      return false;
    }
    toggleCancellingSubscription(true);
    try {
      await cancelCurrentSubscription(values);
      addAlert({
        type: eAlertType.SUCCESS,
        message: t('messages.subscriptionScheduledToEnd'),
      });
      return true;
    } catch (error) {
      const code = readBillingRefusalCode(error);
      addAlert({
        type: eAlertType.ERROR,
        message: t(
          code && OWNER_REQUIRED_REFUSAL_CODES.includes(code)
            ? 'messages.cancelSubscriptionOwnerRequired'
            : 'messages.cancelSubscriptionFailed',
        ),
      });
      return false;
    } finally {
      resetCancellationModalStates();
    }
  }

  /**
   * @param preferredPaymentMethodId - card the caller wants selected once the
   * list lands (the freshly added one). Without it the provider default wins,
   * which is the card that just failed.
   * @param prefetchedResponse - RET-1923 (D3): when the caller already has a
   * settled `getPaymentMethods` response (e.g. `addPaymentMethod`'s
   * `retryUntil` loop, which re-fetches until the newly-added card shows up
   * or the retry budget runs out), pass it here so the list is applied to
   * state exactly once instead of firing a second, redundant network call.
   */
  async function retrievePaymentMethods(
    preferredPaymentMethodId?: string,
    prefetchedResponse?: Awaited<
      ReturnType<typeof SubscriptionManagerService.getPaymentMethods>
    >,
  ) {
    if (!currentWorkspace?.id) {
      // RET-2428: nothing will answer, so a retry that set 'loading' must not
      // leave the spinner up forever.
      setPaymentMethodsLoadState((state) =>
        state === 'loading' ? 'failed' : state,
      );
      return;
    }
    const requestWorkspaceId = currentWorkspace.id;
    // RET-2428: an answer for a workspace that is no longer the current one
    // must not write its cards, its trial card or its load state.
    const isStale = () =>
      currentWorkspaceIdRef.current !== requestWorkspaceId;
    try {
      const paymentMethodsResponse =
        prefetchedResponse ??
        (await SubscriptionManagerService.getPaymentMethods(
          requestWorkspaceId,
        ));
      if (isStale()) return;

      if (isEmpty(paymentMethodsResponse.paymentMethods)) {
        setSelectedPaymentMethod(INITIAL_PAYMENT_METHOD);
        setCurrentPaymentMethods([]);
        setPaymentMethodsLoadState('loaded');
        setPaymentMethodDefaults({});
        setTrialChargePaymentMethodId(null);
        return;
      }
      setCurrentPaymentMethods(paymentMethodsResponse.paymentMethods);
      setPaymentMethodsLoadState('loaded');
      setTrialChargePaymentMethodId(
        paymentMethodsResponse.trialChargePaymentMethodId ?? null,
      );
      setPaymentMethodDefaults({
        customerDefaultPaymentMethodId:
          paymentMethodsResponse.defaultPaymentMethodId || undefined,
        planPaymentMethodId:
          paymentMethodsResponse.planPaymentMethodId || undefined,
      });

      setPaymentMethodsOptions(
        paymentMethodsResponse.paymentMethods.map((method) => ({
          value: method.paymentMethodId,
          brand: method.brand,
          lastDigits: method.lastDigits,
        })),
      );

      const paymentMethodSelection = resolvePaymentMethodSelection({
        paymentMethods: paymentMethodsResponse.paymentMethods,
        defaultPaymentMethodId: paymentMethodsResponse.defaultPaymentMethodId,
        currentSelectionId: selectedPaymentMethod.paymentMethodId,
        preferredPaymentMethodId,
      });

      if (!isUndefined(paymentMethodSelection)) {
        setSelectedPaymentMethod(paymentMethodSelection);
      }
    } catch (err) {
      if (isStale()) return;
      // RET-2428: the list in state is no longer known to be the customer's.
      setPaymentMethodsLoadState('failed');
      addAlert({
        type: eAlertType.ERROR,
        message: `${err}`,
      });
    }
  }

  // RET-2428: the retry the trial confirmation offers after a failed load.
  async function reloadPaymentMethods() {
    setPaymentMethodsLoadState('loading');
    // RET-1974: the wallet calls this after making its own card the default.
    preferredPaymentMethodRef.current = undefined;
    await retrievePaymentMethods();
  }

  const setSelectedPaymentMethodOnClick = async (
    isAllowedClick: boolean,
    paymentMethod: tPaymentMethod,
  ) => {
    // RET-1974: a pick while a bank return is promoting its card would be
    // overwritten by that promotion.
    if (
      !currentWorkspace?.id ||
      loadingPaymentMethodRequest ||
      redirectReturnPendingRef.current ||
      !isAllowedClick
    ) {
      return;
    }
    togglePaymentMethodRequest(true);
    try {
      const promotionOutcome = resolvePaymentMethodDefaultOutcome(
        await SubscriptionManagerService.makePaymentMethodDefault(
          paymentMethod.paymentMethodId,
          currentWorkspace?.id,
        ),
        isTrialSubscription,
      );
      // RET-2428: a card billing kept WITHOUT making it the default must not
      // become the selection either: checkout, debt payment and the top-up
      // would show or charge it as if it were. The selection follows the
      // billing default, re-read below.
      const savedWithoutDefault = promotionOutcome === 'savedWithoutDefault';
      if (!savedWithoutDefault) {
        setSelectedPaymentMethod(paymentMethod);
        // RET-1974 (R7): the checkout keeps the card picked there.
        if (showCheckoutView) {
          preferredPaymentMethodRef.current = {
            workspaceId: currentWorkspace.id,
            paymentMethodId: paymentMethod.paymentMethodId,
          };
        }
      }

      // RET-2428: in a trial the customer has not confirmed, billing keeps
      // the card without making it the default, so «predeterminada
      // actualizada» would be false. Same notice as adding a card there.
      addAlert(
        savedWithoutDefault
          ? {
              type: eAlertType.SUCCESS,
              title: t(PAYMENT_METHOD_ADDED_TITLE_KEY),
              message: t(PAYMENT_METHOD_SAVED_WITHOUT_DEFAULT_KEY),
            }
          : {
              type: eAlertType.SUCCESS,
              message: t('messages.paymentMethodUpdated'),
            },
      );

      // RET-2027: billing also moved the card of every live subscription, so
      // the plan's card changed with it — re-read which card is which instead
      // of assuming it (a subscription billing skips keeps its card).
      await retrievePaymentMethods(
        savedWithoutDefault ? undefined : paymentMethod.paymentMethodId,
      );
    } catch (e) {
      // RET-2347, RET-2155: the same refusals as in addPaymentMethod, on a
      // card that was already saved. Nothing is deleted: the card stays in the
      // list, and the alert says why it cannot be the default (its brand
      // cannot be charged, or the bank refused it).
      if (e instanceof PaymentMethodDefaultRefusedError) {
        const { refusal } = e;
        addAlert(resolvePaymentMethodDefaultRefusalNotice(refusal, t));
        // Billing puts the previous card back after a bank refusal, and then
        // nothing changed. When it says it could not, a subscription is left
        // on the refused card: read the list again so the badges show which
        // card each thing charges, and the customer can pick another one.
        if (
          refusal.status === 'paymentMethodDeclined' &&
          !refusal.previousCardRestored
        ) {
          preferredPaymentMethodRef.current = undefined;
          await retrievePaymentMethods();
        }
        togglePaymentMethodRequest(false);
        return;
      }
      const error = e as Error;
      LLog.error(
        {
          component: LOG_COMPONENTS.SUBSCRIPTION,
          method: 'SubscriptionManager.hook',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error al actualizar método de pago',
        error,
      );
      addAlert({
        type: eAlertType.ERROR,
        message: `${error.message}`,
      });
    }
    togglePaymentMethodRequest(false);
  };

  /**
   * Pays pending debts for the workspace
   * Handles 3D Secure authentication if required
   */
  async function payDebts() {
    if (!currentWorkspace?.id || processingPayment || !stripe) {
      return;
    }
    toggleProcessingPayment(true);
    try {
      const response = await SubscriptionManagerService.payPendingDebt(
        currentWorkspace.id,
        selectedPaymentMethod.paymentMethodId,
      );

      // Check if 3D Secure authentication is required
      if (
        response.data.requires_action &&
        response.data.payment_intent_client_secret
      ) {
        LLog.info(
          {
            component: LOG_COMPONENTS.UI,
            method: 'SubscriptionManager.hook',
            operation: LOG_OPERATIONS.UI_INTERACTION,
          },
          'Starting 3D Secure authentication for pending debt payment',
        );

        // Trigger 3D Secure authentication
        const result = await stripe.confirmPayment({
          clientSecret: response.data.payment_intent_client_secret,
          redirect: 'if_required' as const,
        });

        if (result?.error) {
          // Authentication failed or was cancelled
          LLog.error(
            {
              component: LOG_COMPONENTS.UI,
              method: 'SubscriptionManager.hook',
              operation: LOG_OPERATIONS.ERROR,
            },
            'Error during 3D Secure authentication for pending debt payment',
            result.error as unknown as Error,
          );

          // Same 3DS reasoning as onUpgradeSubscription: Stripe.js hands back an
          // English message, so resolve localized copy from its code instead.
          addAlert({
            type: eAlertType.ERROR,
            message: resolveBillingErrorMessage(
              stripeJsErrorToBillingPayload(result.error),
              t,
            ),
          });

          toggleProcessingPayment(false);
          return;
        }

        LLog.info(
          {
            component: LOG_COMPONENTS.UI,
            method: 'SubscriptionManager.hook',
            operation: LOG_OPERATIONS.UI_INTERACTION,
          },
          '3D Secure authentication completed successfully for pending debt payment',
        );
      }

      // Wait for the payment to be processed
      await new Promise<void>((resolve) =>
        setTimeout(async () => {
          await getSummary();
          resolve();
        }, 2000),
      );

      addAlert({
        type: eAlertType.SUCCESS,
        message: t('messages.subscriptionPaid'),
      });
    } catch (e) {
      const error = e as Error;
      LLog.error(
        {
          component: LOG_COMPONENTS.SUBSCRIPTION,
          method: 'SubscriptionManager.hook',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error processing pending debt payment',
        error,
      );
      addAlert({
        type: eAlertType.ERROR,
        message: t(`errors.${error.message}`),
      });
    }
    toggleProcessingPayment(false);
  }

  async function deleteCard(paymentMethodId: string) {
    // RET-1974: deleting while a bank return waits for its card in the list
    // would spend the whole re-read on a card that is gone.
    if (
      !currentWorkspace?.id ||
      loadingPaymentMethodRequest ||
      redirectReturnPendingRef.current
    ) {
      return;
    }
    togglePaymentMethodRequest(true);
    try {
      await SubscriptionManagerService.deletePaymentMethod(
        paymentMethodId,
        currentWorkspace.id,
      );
      // Prune optimistically so the row disappears at once, then reconcile:
      // the local prune never touched selectedPaymentMethod, so deleting the
      // selected card left every later charge sending a dead payment method id.
      // The refetch drops a selection that is no longer on the customer.
      setCurrentPaymentMethods(
        paymentMethods.filter((pm) => pm.paymentMethodId !== paymentMethodId),
      );
      await retrievePaymentMethods();
      addAlert({
        type: eAlertType.SUCCESS,
        message: t('messages.paymentMethodDeleted'),
      });
    } catch (e) {
      const error = e as Error;
      LLog.error(
        {
          component: LOG_COMPONENTS.SUBSCRIPTION,
          method: 'SubscriptionManager.hook',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error al eliminar tarjeta',
        error,
      );
      addAlert({
        type: eAlertType.ERROR,
        message: `${error.message}`,
      });
    }
    togglePaymentMethodRequest(false);
  }

  function getSeatPriceFromPlans() {
    const fullPlan = plans?.[period].find(
      (plan) => plan.name === ePlanType.FULL,
    );
    const monthlySeatsPrice = fullPlan?.extraSeatPrice || 0;

    switch (subscription.frequency) {
      case ePlanFrequency.QUARTERLY:
        return monthlySeatsPrice * 3;
      case ePlanFrequency.BIANNUAL:
        return monthlySeatsPrice * 6;
      case ePlanFrequency.ANNUALLY:
        return monthlySeatsPrice * 12;
      case ePlanFrequency.TRIAL:
      case ePlanFrequency.MONTHLY:
      default:
        return monthlySeatsPrice;
    }
  }

  async function convertTrialToSubscription(
    paymentMethodId: string,
    authId: string,
  ) {
    if (
      !currentWorkspace?.id ||
      processingPayment ||
      !paymentMethodId ||
      !authId ||
      !stripe
    ) {
      return;
    }
    // RET-1974: the trial's card becomes the plan's; the selection follows it.
    preferredPaymentMethodRef.current = undefined;
    toggleProcessingPayment(true);
    try {
      const response = await SubscriptionService.convertTrialToSubscription(
        currentWorkspace.id,
        paymentMethodId,
      );

      // Check if 3D Secure authentication is required
      if (response.requires_action && response.payment_intent_client_secret) {
        LLog.info(
          {
            component: LOG_COMPONENTS.UI,
            method: 'SubscriptionManager.hook',
            operation: LOG_OPERATIONS.UI_INTERACTION,
          },
          'Starting 3D Secure authentication for trial conversion',
        );

        // Trigger 3D Secure authentication
        const result = await stripe.confirmPayment({
          clientSecret: response.payment_intent_client_secret,
          redirect: 'if_required' as const,
        });

        if (result?.error) {
          // Authentication failed or was cancelled
          LLog.error(
            {
              component: LOG_COMPONENTS.UI,
              method: 'SubscriptionManager.hook',
              operation: LOG_OPERATIONS.ERROR,
            },
            'Error during 3D Secure authentication for trial conversion',
            result.error as unknown as Error,
          );

          // Same 3DS reasoning as onUpgradeSubscription: Stripe.js hands back an
          // English message, so resolve localized copy from its code instead.
          addAlert({
            type: eAlertType.ERROR,
            message: resolveBillingErrorMessage(
              stripeJsErrorToBillingPayload(result.error),
              t,
            ),
          });

          toggleProcessingPayment(false);
          return;
        }

        LLog.info(
          {
            component: LOG_COMPONENTS.UI,
            method: 'SubscriptionManager.hook',
            operation: LOG_OPERATIONS.UI_INTERACTION,
          },
          '3D Secure authentication completed successfully for trial conversion',
        );

        // After successful 3D Secure authentication, confirm the payment with the backend
        if (result?.paymentIntent) {
          await SubscriptionManagerService.confirm3DSecurePayment(
            currentWorkspace.id,
            result.paymentIntent.id,
            result.paymentIntent.status,
          );
        }
      }

      await new Promise<void>((resolve) =>
        setTimeout(async () => {
          await getSummary();
          resolve();
        }, 2000),
      );
      addAlert({
        type: eAlertType.SUCCESS,
        message: t('messages.trialConvertedToSubscription'),
      });
    } catch (e) {
      const error = e as Error;
      LLog.error(
        {
          component: LOG_COMPONENTS.SUBSCRIPTION,
          method: 'SubscriptionManager.hook',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error al convertir trial a suscripción',
        error,
      );
      addAlert({
        type: eAlertType.ERROR,
        message: `${error.message}`,
      });
    }
    toggleProcessingPayment(false);
  }

  async function fetchInvoicePreview(signal: AbortSignal) {
    const action = shouldFetchInvoicePreview({
      hasWorkspaceId: !!currentWorkspace?.id,
      hasPlanToBuy: !!planToBuy,
      isClient,
      subscriptionStatus: subscription?.status,
      subscriptionPlanCode: subscription?.planCode,
      subscriptionFrequency: subscription?.frequency,
      planToBuyPlanCode: planToBuy?.planCode,
      planToBuyFrequency: planToBuy?.frequencyName,
    });

    if (action === 'reset' || !planToBuy || !currentWorkspace?.id) {
      setInvoicePreview(null);
      setLoadingInvoicePreview(false);
      return;
    }

    setLoadingInvoicePreview(true);
    try {

      // Build request payload. RET-1944: the construction lives in
      // `buildInvoicePreviewRequest` so it can be tested without mounting this hook.
      const request = buildInvoicePreviewRequest({
        planToBuy,
        subscriptionFrequency: subscription.frequency,
        subscriptionCurrency: subscription.currency,
        subscriptionPlanCode: subscription.planCode,
        extraSeats,
        watermarkRemoval,
        isWalletEnabled,
        monthlyCredits,
        selectedAddonProducts,
      });

      const preview = await SubscriptionManagerService.getInvoicePreview(
        currentWorkspace.id,
        request,
        signal,
      );

      setInvoicePreview(preview);
      setLoadingInvoicePreview(false);
    } catch (error) {
      // Don't show error alerts for cancelled requests
      if ((error as AxiosError).code === 'ERR_CANCELED') {
        // Keep loading state true since a new request should follow immediately
        setLoadingInvoicePreview(true);
        return;
      }

      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'SubscriptionManager.hook',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error fetching invoice preview',
        error as Error,
      );
      addAlert({
        type: eAlertType.ERROR,
        message: t('messages.errorFetchingInvoicePreview'),
      });
      setLoadingInvoicePreview(false);
    }
  }

  return {
    pastDuePayment,
    availableProductsForPurchase,
    selectedAddonProducts,
    addSelectedAddonProduct,
    removeSelectedAddonProduct,
    toggleSelectedAddonProduct,
    plans,
    checkoutSelectedPlan,
    showCheckoutView,
    showPlansView,
    togglePlansView,
    planToBuy,
    showAddPaymentMethodModal,
    toggleAddPaymentMethodModal,
    showManageUsersModal,
    toggleManageUsersModal,
    addPaymentMethod,
    addingPaymentMethod,
    cardSavedAsDefaultToken,
    cardSavedAsDefaultPaymentMethodId,
    cardSavedWithoutDefaultToken,
    cardSavedWithoutDefaultPaymentMethodId,
    openAddPaymentMethodModal,
    addPaymentMethodOptions,
    cardSavedForCaller,
    showPaymentMethodsManager,
    editPaymentMethods,
    extraSeats,
    setExtraSeats,
    onCreateSubscription,
    onUpgradeSubscription,
    handlePayment,
    returnToOverview,
    returnFromCheckout,
    updateWatermarkPlan,
    updateSeatsPlan,
    updatingPlan,
    processingPayment,
    couponCode,
    setCouponCode,
    appliedCouponDiscount,
    couponDiscountType,
    couponDuration,
    couponDurationInMonths,
    couponError,
    setCouponError,
    applyingCoupon,
    applyCoupon,
    removeCoupon,
    isUpgradeContext,
    selectedPaymentMethod,
    paymentMethods,
    paymentMethodsLoadState,
    reloadPaymentMethods,
    trialChargePaymentMethodId,
    paymentMethodDefaults,
    setSelectedPaymentMethodOnClick,
    loadingPaymentMethodRequest,
    payDebts,
    deleteCard,
    showPaymentCompletedModal,
    togglePaymentCompletedModal,
    cancelSubscriptionFormik,
    showCancelSubscriptionModal,
    toggleCancelSubscriptionModal,
    cancellingSubscription,
    resetCancellationModalStates,
    reactivatePlan,
    filteredAddOns,
    getSeatPriceFromPlans,
    showManageWatermarkModal,
    toggleManageWatermarkModal,
    showManageAdvancedLeadbotModal,
    toggleManageAdvancedLeadbotModal,
    updateAdvancedLeadbotPlan,
    showManageLeadLimitAddonModal,
    toggleManageLeadLimitAddonModal,
    updateLeadLimitAddonPlan,
    isTrial,
    setIsTrial,
    products,
    monthlyCredits,
    setMonthlyCredits,
    baseMonthlyCredits,
    showTrialCheckoutView,
    toggleTrialCheckoutView,
    paymentMethodsOptions,
    showOnlyWAPIPlans,
    period,
    setPeriod,
    convertTrialToSubscription,
    invoicePreview,
    loadingInvoicePreview,
    fetchInvoicePreview,
    generalCancellationProperties,
  };
}

export default useSubscriptionManager;
